import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Moon, Clock, CalendarDays, History, DollarSign, PiggyBank, MessageSquare,
  ShieldCheck, FileText, Upload, LogOut, CheckCircle2, Loader2, AlertTriangle, ChevronRight,
  MapPin, Search, ChevronDown, Flag, Send, LayoutGrid, Lock, Users, Landmark, BookOpen, Download,
  ClipboardCheck,
} from 'lucide-react';
import MyDmsPortalTab, { fetchDmsAttention } from '../components/MyDmsPortalTab';
import VevoConsentPrompt from '../components/VevoConsentPrompt';
import ChuteLayoutTab from './ChuteLayoutTab';
import SorterRosterTab from './SorterRosterTab';
import BriefText from '../components/BriefText';
import CupNoticeCard from '../components/CupNoticeCard';
import PeakAvailabilityTab, { PeakAvailabilityBanner, usePeakOutstanding } from './PeakAvailabilityTab';
import PeakAvailabilityView from './PeakAvailabilityView';

// ── Night Sorters portal ──────────────────────────────────────────────────────
// Employee-facing portal for casual night sorters (PAYG), a sibling of the
// contractor DriverLogPage. P0 ships login + the My Documents onboarding tab
// (TFN declaration, super choice, document upload, Fair Work statements). The
// other tabs are scaffolded placeholders delivered in later phases:
//   Shifts/Clock/History → P1 · Payslips/Super → P2 · Messages → P4.

const SESSION_KEY = 'hp_sorter_session';
const SESSION_EXPIRED_MSG = 'Your session has expired — please sign in again.';

// The UI session (localStorage, no expiry) can outlive the httpOnly hp_sorter cookie
// (7 days): the page looks signed in while every gated call 403s "Sign-in required".
// When a helper sees that 403 it broadcasts here, and the page drops the stale session
// and returns to the sign-in form instead of surfacing raw errors on every tab.
function broadcastSessionExpired() { try { window.dispatchEvent(new Event('hp-sorter-session-expired')); } catch {} }
function checkSessionExpired(status, data) {
  if (status === 403 && data && data.error === 'Sign-in required') broadcastSessionExpired();
  return data;
}

const S = {
  page: { minHeight: '100vh', background: 'var(--hp-black)', color: 'var(--hp-text)', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-body)' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--hp-border)', background: 'var(--hp-surface)' },
  brand: { fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.04em', color: 'var(--hp-gold)' },
  wrap: { width: '100%', maxWidth: 560, margin: '0 auto', padding: '20px 16px 60px' },
  card: { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg, 14px)', padding: '22px 20px', marginBottom: 16 },
  label: { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },
  input: { width: '100%', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius, 8px)', color: 'var(--hp-text)', fontSize: '1rem', padding: '12px 14px', marginBottom: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' },
  bigBtn: (bg) => ({ width: '100%', padding: '16px', background: bg, color: 'var(--hp-black)', fontWeight: 800, fontSize: '1rem', border: 'none', borderRadius: 'var(--radius-lg, 14px)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }),
  ghostBtn: { padding: '10px 14px', background: 'transparent', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' },
  tabBar: { display: 'flex', overflowX: 'auto', borderBottom: '2px solid var(--hp-border)', background: 'var(--hp-surface)' },
  tab: (active) => ({ flex: '0 0 auto', padding: '12px 14px', background: 'transparent', border: 'none', borderBottom: active ? '2px solid var(--hp-gold)' : '2px solid transparent', marginBottom: -2, color: active ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' }),
  sectionTitle: { fontFamily: 'var(--font-display)', fontSize: '1.15rem', letterSpacing: '0.03em', margin: '0 0 4px' },
  sub: { fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '0 0 16px', lineHeight: 1.5 },
  pill: (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg }),
  row: { display: 'flex', gap: 10 },
};

// ORDER MATTERS ON A PHONE. The strip scrolls, and only the first three tabs are reliably
// on screen at 430px — a red count on a tab nobody can see is not a prompt. "My hours" sits
// second for exactly that reason (Paul, 3 Sep 2026). Clock moving right costs little: the
// depot QR opens /sorters?clock=<token> and lands straight on that tab, so it is reached by
// scanning far more often than by tapping.
const TABS = [
  { key: 'shifts',   label: 'My Shifts',  icon: CalendarDays, phase: 'P1' },
  { key: 'history',  label: 'My hours',   icon: History,      phase: 'P1' },
  { key: 'roster',   label: 'Team Roster', icon: Users },
  { key: 'clock',    label: 'Clock',      icon: Clock,        phase: 'P1' },
  { key: 'guide',    label: 'Sort Guide', icon: MapPin },
  { key: 'layout',   label: 'Chute Layout', icon: LayoutGrid },
  { key: 'peak',     label: 'Peak availability', icon: CalendarDays },
  { key: 'timeoff',  label: 'Nights off', icon: CalendarDays },
  { key: 'payslips', label: 'Payslips',   icon: DollarSign,   phase: 'P2' },
  { key: 'super',    label: 'Super',      icon: PiggyBank,    phase: 'P2' },
  { key: 'docs',     label: 'Documents',  icon: FileText },
  { key: 'messages', label: 'Messages',   icon: MessageSquare, phase: 'P4' },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ── Vault unlock token (personal step-up password) ────────────────────────────
// The Documents tab is gated by a personal password each sorter sets on first use.
// A successful unlock returns a 15-min token that every sensitive call carries; we
// keep it in sessionStorage (per login) so moving between tabs doesn't re-prompt,
// and drop it once it expires or the sorter taps Lock.
const unlockKey = (login) => 'hp_sorter_vault_unlock_' + String(login || '').toUpperCase();
const tokenExp = (tok) => { try { return parseInt(String(tok).split('.')[1], 10) || 0; } catch { return 0; } };
function readUnlock(login) {
  try {
    const t = sessionStorage.getItem(unlockKey(login));
    if (t && tokenExp(t) > Math.floor(Date.now() / 1000) + 5) return t; // 5s guard against expiry mid-call
    if (t) sessionStorage.removeItem(unlockKey(login));
  } catch {}
  return '';
}
const saveUnlock = (login, tok) => { try { sessionStorage.setItem(unlockKey(login), tok); } catch {} };
const clearUnlock = (login) => { try { sessionStorage.removeItem(unlockKey(login)); } catch {} };
function vaultFetch(body) {
  return fetch('/.netlify/functions/sorter-vault', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }).then(r => r.json().then(d => checkSessionExpired(r.status, d)));
}

export default function SortersPortalPage() {
  const [sorter, setSorter] = useState(null);
  // Scanning the depot QR opens /sorters?clock=<token>; carry the token through
  // login and default straight to the Clock tab so a scan lands on clock on/off.
  const [clockToken] = useState(() => { try { return new URLSearchParams(window.location.search).get('clock') || ''; } catch { return ''; } });
  // Which poster was scanned — the LOG IN (green) and LOG OUT (red) codes carry
  // &dir=in|out so the clock screen knows the direction and can flag the wrong code.
  const [clockDir] = useState(() => { try { return new URLSearchParams(window.location.search).get('dir') || ''; } catch { return ''; } });
  // Landing tab: My Shifts (Paul, 3 Sep 2026 — was Documents). Documents was the landing
  // to push the onboarding paperwork, and that job now belongs to TaxSuperBanner, which
  // shows on every other tab the moment the office actually asks for something and jumps
  // straight there. A scan of the depot QR still overrides everything and opens Clock.
  const [tab, setTab] = useState(() => (clockToken ? 'clock' : 'shifts'));
  // How many compliance documents need this sorter's attention (missing/expiring/
  // requested) — drives the red dot on the Documents tab, same as the driver portal.
  const [docsAttention, setDocsAttention] = useState(0);
  const [peakTick, setPeakTick] = useState(0);
  const peakOutstanding = usePeakOutstanding(sorter && sorter.login, peakTick);
  // This pay period's shifts + the sorter's own sign-off on each. Held here, not in the
  // tab, because the red dot and the end-of-period banner read the same numbers the
  // review list does — two fetches would let the dot and the list disagree.
  const [review, setReview] = useState(null);
  // Which pay period My hours is showing — '' means the current one. It lives in the shell
  // rather than the tab because the banner on every OTHER tab has to be able to send
  // someone straight to the closed period it is nagging them about.
  const [periodFrom, setPeriodFrom] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ login: '', pin: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Restore a UI session (the httpOnly cookie still gates server calls), then ping
  // sorter-auth to slide the cookie's 7-day expiry forward. A dead cookie answers
  // SESSION_EXPIRED and we drop straight to the sign-in form rather than letting a
  // signed-in-looking page 403 on every action.
  useEffect(() => {
    let s = null;
    try { s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch {}
    if (!s || !s.login) return;
    setSorter(s);
    fetch('/.netlify/functions/sorter-auth', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'refresh' }),
    }).then(r => r.json()).then(d => { if (d && d.ok === false) broadcastSessionExpired(); }).catch(() => {});
  }, []);

  const loadReview = useCallback(async () => {
    if (!sorter || !sorter.login) return;
    setReviewBusy(true);
    try { const r = await appsscript('getSorterShiftReview', { login: sorter.login, ...(periodFrom ? { from: periodFrom } : {}) }); setReview(r && r.success ? r : null); }
    catch (_) { /* the tab shows its own loading state; a dot we can't compute just doesn't show */ }
    finally { setReviewBusy(false); }
  }, [sorter, periodFrom]);
  useEffect(() => { loadReview(); }, [loadReview]);

  // The Documents badge, counted from the shell rather than from the tab. The portal used
  // to LAND on Documents, so opening the app was what loaded this; now that it lands on My
  // Shifts, a count computed inside the tab would only ever appear after someone had
  // already gone looking. The tab still reports its own count while it is open.
  useEffect(() => {
    if (!sorter || !sorter.login) return;
    let alive = true;
    fetchDmsAttention().then(n => { if (alive) setDocsAttention(n); }).catch(() => {});
    return () => { alive = false; };
  }, [sorter]);

  // A gated call anywhere in the portal hit 403 "Sign-in required" — the cookie is
  // gone even though the UI session survived. Sign the sorter out with an explanation.
  useEffect(() => {
    const onExpired = () => {
      localStorage.removeItem(SESSION_KEY);
      setSorter(null);
      setLoginForm({ login: '', pin: '' });
      setError(SESSION_EXPIRED_MSG);
    };
    window.addEventListener('hp-sorter-session-expired', onExpired);
    return () => window.removeEventListener('hp-sorter-session-expired', onExpired);
  }, []);

  async function handleLogin() {
    setError(''); setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/sorter-auth', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: loginForm.login, pin: loginForm.pin }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Incorrect login or PIN.');
      localStorage.setItem(SESSION_KEY, JSON.stringify(data));
      setSorter(data);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function logout() { localStorage.removeItem(SESSION_KEY); setSorter(null); setLoginForm({ login: '', pin: '' }); }

  if (!sorter) {
    return (
      <div style={S.page}>
        <div style={S.header}><Moon size={22} color="var(--hp-gold)" /><span style={S.brand}>Hollypaul · Night Sorters</span></div>
        <div style={{ ...S.wrap, maxWidth: 420 }}>
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Sign in</h2>
            <p style={S.sub}>Use the login and PIN your supervisor gave you.</p>
            <label style={S.label}>Login</label>
            <input style={S.input} value={loginForm.login} autoCapitalize="characters"
              onChange={e => setLoginForm(f => ({ ...f, login: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="e.g. SMITHJ" />
            <label style={S.label}>PIN</label>
            <input style={S.input} type="password" inputMode="numeric" value={loginForm.pin}
              onChange={e => setLoginForm(f => ({ ...f, pin: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="••••••" />
            {error && <div style={{ ...S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), marginBottom: 14 }}><AlertTriangle size={13} />{error}</div>}
            <button style={S.bigBtn('var(--hp-gold)')} disabled={busy} onClick={handleLogin}>
              {busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : <>Sign in <ChevronRight size={18} /></>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <VevoConsentPrompt login={sorter.login} />
      <div style={S.header}>
        <Moon size={22} color="var(--hp-gold)" />
        <div style={{ flex: 1 }}>
          <div style={S.brand}>Night Sorters</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{sorter.name} · {sorter.classification}{sorter.facility ? ' · ' + sorter.facility : ''}</div>
        </div>
        <button style={S.ghostBtn} onClick={logout}><LogOut size={14} /> Sign out</button>
      </div>

      <div style={S.tabBar}>
        {TABS.map(t => (
          <button key={t.key} style={S.tab(tab === t.key)} onClick={() => setTab(t.key)}>
            <t.icon size={14} /> {t.label}
            {t.key === 'docs' && docsAttention > 0 && <TabDot n={docsAttention} />}
            {t.key === 'peak' && peakOutstanding > 0 && <TabDot n={peakOutstanding} />}
            {/* Every period they can still answer, not just the one on screen — a dot that
                counted the visible period would go out the moment someone switched to a
                period they had finished, with a closed one still unchecked behind it. */}
            {t.key === 'history' && review && (review.outstanding ?? review.counts.awaiting) > 0 && <TabDot n={review.outstanding ?? review.counts.awaiting} />}
          </button>
        ))}
      </div>

      <div style={S.wrap}>
        {/* Team Roster carries its own richer "Tonight" card (clock status + finish time),
            so the global banner would just repeat it directly above. */}
        {/* Melbourne Cup sweep notice — sorters can't get pushes, so it shows on every tab. */}
        <CupNoticeCard />
        <RosterChangeBanner login={sorter.login} />
        {tab !== 'roster' && <FinishTimeBanner />}
        {tab !== 'peak' && <PeakAvailabilityBanner login={sorter.login} refreshKey={peakTick} onOpen={() => setTab('peak')} />}
        {tab !== 'docs' && <TaxSuperBanner login={sorter.login} onOpenDocs={() => setTab('docs')} />}
        {tab !== 'history' && <ReviewBanner review={review} onOpen={(from) => { setPeriodFrom(from || ''); setTab('history'); }} />}
        {tab === 'docs' ? <DocumentsTab sorter={sorter} onDocsCount={setDocsAttention} />
          : tab === 'shifts' ? <MyShiftsTab sorter={sorter} />
          : tab === 'roster' ? <SorterRosterTab sorter={sorter} />
          : tab === 'clock' ? <ClockTab sorter={sorter} qrToken={clockToken} qrDir={clockDir} />
          : tab === 'guide' ? <SortGuideTab sorter={sorter} />
          : tab === 'layout' ? <ChuteLayoutTab sorter={sorter} />
          : tab === 'history' ? <HistoryTab sorter={sorter} review={review} reload={loadReview} switching={reviewBusy} onPeriod={setPeriodFrom} />
          : tab === 'peak' ? <PeakAvailabilityTab sorter={sorter} onAnswered={() => setPeakTick(t => t + 1)} teamView={<PeakAvailabilityView />} />
          : tab === 'timeoff' ? <NightsOffTab sorter={sorter} />
          : tab === 'payslips' ? <VaultGate login={sorter.login} what="your payslips and pay details">{(unlock, relock) => <PayslipsTab sorter={sorter} unlock={unlock} onLocked={relock} />}</VaultGate>
          : tab === 'super' ? <VaultGate login={sorter.login} what="your super and pay details">{(unlock, relock) => <SuperTab sorter={sorter} unlock={unlock} onLocked={relock} />}</VaultGate>
          : <Placeholder tab={TABS.find(t => t.key === tab)} />}
      </div>
    </div>
  );
}

// The red count on a tab — documents needing attention, shifts needing checking.
function TabDot({ n }) {
  return <span style={{ marginLeft: 5, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: 'var(--hp-red, #EF4444)', color: '#fff', fontSize: '0.66rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>;
}

function Placeholder({ tab }) {
  const phaseLabel = {
    P1: 'Rostering, clock in/out & history — coming in Phase 1',
    P2: 'Payslips & superannuation — coming in Phase 2 (independent payroll engine)',
    P4: 'Messaging — coming in Phase 4',
  }[tab?.phase] || 'Coming soon';
  return (
    <div style={{ ...S.card, textAlign: 'center', padding: '48px 20px' }}>
      {tab?.icon ? <tab.icon size={32} color="var(--hp-dim)" /> : null}
      <h2 style={{ ...S.sectionTitle, marginTop: 12 }}>{tab?.label}</h2>
      <p style={S.sub}>{phaseLabel}</p>
    </div>
  );
}

// ── Nights off — request nights you'd like free (best-endeavours) ─────────────
function NightsOffTab({ sorter }) {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 5000); };
  const load = () => appsscript('listInability', {}).then(r => setRows(r && r.success ? r.requests : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [sorter.login]);
  const pad = (n) => String(n).padStart(2, '0');
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const fmt = (iso) => { try { return new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return iso; } };

  async function submit() {
    if (!date) { flash('Pick a date first.', false); return; }
    setBusy(true);
    try {
      const r = await appsscript('submitInability', { date, reason });
      if (!r || !r.success) throw new Error((r && r.error) || 'Could not send your request.');
      setDate(''); setReason('');
      flash("Sent. We'll do our best — but you may still be rostered that night.");
      load();
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }
  async function cancel(id) {
    const r = await appsscript('cancelInability', { id });
    if (r && r.success) setRows(list => list.filter(x => x.id !== id));
  }

  return (
    <>
      <div style={S.card}>
        <h2 style={S.sectionTitle}>Request a night off</h2>
        <p style={S.sub}>Let the office know a night you'd like free. We do our best to work around it — but a request isn't a guarantee, and you may still be rostered if you're needed.</p>
        <label style={S.label}>Night</label>
        <input type="date" style={S.input} min={todayStr} value={date} onChange={e => setDate(e.target.value)} />
        <label style={S.label}>Reason (optional)</label>
        <input style={S.input} value={reason} placeholder="e.g. family event" onChange={e => setReason(e.target.value)} />
        {msg && <div style={{ ...S.pill(msg.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', msg.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), marginBottom: 12, padding: '8px 12px' }}>{msg.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}{msg.text}</div>}
        <button style={S.bigBtn('var(--hp-gold)')} disabled={busy || !date} onClick={submit}>{busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : <>Send request <ChevronRight size={18} /></>}</button>
      </div>
      <div style={S.card}>
        <h2 style={S.sectionTitle}>Your requests</h2>
        {rows.length === 0 ? (
          <p style={S.sub}>No nights-off requests yet.</p>
        ) : rows.map(x => (
          <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid var(--hp-border)', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', minWidth: 110 }}>{fmt(x.date)}</span>
            {x.status === 'Granted'
              ? <span style={S.pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={12} /> Night off approved</span>
              : x.status === 'NotMet'
                ? <span style={S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')}><AlertTriangle size={12} /> Still rostered</span>
                : <span style={S.pill('rgba(245,158,11,0.14)', 'var(--hp-gold)')}>Pending</span>}
            {x.status === 'NotMet' && x.reviewNote && <span style={{ flex: 1, fontSize: '0.8rem', color: 'var(--hp-dim)', minWidth: 120 }}>Office: “{x.reviewNote}”</span>}
            {x.status === 'Requested'
              ? <button style={{ ...S.ghostBtn, marginLeft: 'auto', color: 'var(--hp-red, #EF4444)' }} onClick={() => cancel(x.id)}>Cancel</button>
              : <span style={{ marginLeft: 'auto', fontSize: '0.74rem', color: 'var(--hp-dim)' }}>{x.reason ? `“${x.reason}”` : ''}</span>}
          </div>
        ))}
      </div>
    </>
  );
}

// ── Clock helpers ─────────────────────────────────────────────────────────────
const BREAK_MIN = 30;
function fmtTime(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } }
function fmtDur(ms) { if (ms < 0) ms = 0; const s = Math.floor(ms / 1000); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60; return (h ? h + ':' : '') + String(m).padStart(2, '0') + ':' + String(ss).padStart(2, '0'); }
function haversine(a, b) { const R = 6371000, toRad = x => x * Math.PI / 180; const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng); const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(x)); }
// `acc` = the fix's own accuracy in metres. The server forgives that margin against the
// fence — a phone inside a steel depot routinely reports ±50-150m, and refusing on a fix
// the phone itself doesn't trust is how someone standing at the door gets locked out.
function getPosition() { return new Promise((res, rej) => { if (!navigator.geolocation) return rej(new Error('No GPS on this device.')); navigator.geolocation.getCurrentPosition(p => res({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }), () => rej(new Error('Location permission is needed to clock on.')), { enableHighAccuracy: true, timeout: 12000 }); }); }
// iOS Safari can leave getCurrentPosition hanging with NO callback at all (its `timeout`
// option only starts once the permission prompt is answered), and a stalled fetch on weak
// depot signal never settles either — both left the clock buttons spinning forever
// (Jowher, 14 Aug 2026: couldn't clock off, spinner stuck from 6:12am). Every await on
// the clock path gets a hard deadline so the sorter always sees an error and can retry.
function withTimeout(promise, ms, message) {
  return Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(message)), ms))]);
}
const GPS_TIMEOUT_MSG = "Couldn't get your location. Check Location Services is on for Safari, then try again.";
const NET_TIMEOUT_MSG = "Couldn't reach the server — check your signal and try again.";
// ── One tap is enough ─────────────────────────────────────────────────────
// Night of 14 Sep 2026: four sorters tapped Clock On 24 times between them because every
// server hiccup came back to the PERSON as "wait a moment and try again". Now the phone does
// the trying. A clock tap is kept (in memory and in localStorage, so a reload or a locked
// screen doesn't lose it) and re-sent until the server gives a real answer, for up to
// TAP_WINDOW_MS. Every attempt carries the moment of the tap and the moment it was sent; the
// server records the shift at the tap, not at whichever attempt finally got through
// (resolveTapTime in sorter-shift-sb.js — the two windows must stay equal). Re-sending is
// always safe: clock-on finds the row it already wrote, clock-off answers alreadyOff.
// Only "couldn't reach / couldn't read / couldn't save" is retried — a QR, fence or
// sign-in refusal is a real answer and is shown straight away.
const TAP_WINDOW_MS = 10 * 60000;
const TAP_QUIET_MS = 20000;                        // before this, the button spinner says enough
const TAP_RETRY_STEPS_MS = [1500, 3000, 5000, 8000, 10000]; // then every 10s
const RETRYABLE_GATES = new Set(['LOOKUP_FAILED', 'WRITE_FAILED']);
const PENDING_TAP_KEY = (login) => 'hp_sorter_pending_tap_' + login;
const activeTaps = new Set();                      // tapAt of loops running in this page, across tab switches
const pause = (ms) => new Promise((r) => setTimeout(r, ms));
function readPendingTap(login) { try { return JSON.parse(localStorage.getItem(PENDING_TAP_KEY(login)) || 'null'); } catch { return null; } }
function writePendingTap(login, p) { try { if (p) localStorage.setItem(PENDING_TAP_KEY(login), JSON.stringify(p)); else localStorage.removeItem(PENDING_TAP_KEY(login)); } catch { /* private mode — memory still holds it */ } }
const TAP_VERB = { in: 'clock-on', out: 'clock-off', breakStart: 'break start', breakEnd: 'break end' };

function appsscript(action, payload) { return fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then(r => r.json().then(d => checkSessionExpired(r.status, d))); }

// ── Office briefs — read and acknowledge before the clock tap ────────────────
// The office issues a brief (sorter-briefs-sb.js); a sorter on shift reads it before they
// clock off, and one who wasn't on shift when it went out reads it before their next clock
// on. Each acknowledgement is recorded with its time so the office can see who has read it.
//
// The clock is never held hostage to it: if the briefs can't be read the tap goes straight
// through, and if the acknowledgement can't be saved the tap still goes through (it simply
// asks again next time). Same reason as everywhere on this screen — a clock-off that
// doesn't happen costs the sorter hours.

// The crew's languages. The English text is the brief; a translation is a reading aid, and
// whichever one is on screen when they tap is recorded with the acknowledgement.
const BRIEF_LANGS = [['en', 'English'], ['es', 'Español'], ['zh', '中文'], ['mn', 'Монгол'], ['bn', 'বাংলা']];
const BRIEF_NOTE = {
  es: 'Traducido automáticamente. Si algo no está claro, pregunta a tu team leader.',
  zh: '自动翻译。如有不清楚的地方，请问你的 team leader。',
  mn: 'Автоматаар орчуулсан. Ойлгомжгүй зүйл байвал team leader-ээсээ асуугаарай.',
  bn: 'স্বয়ংক্রিয়ভাবে অনুবাদ করা। কিছু বুঝতে না পারলে আপনার team leader-কে জিজ্ঞেস করুন।',
};
const briefLangKey = (login) => `hp_sorter_brief_lang_${login}`;
function readBriefLang(login) {
  try { const v = localStorage.getItem(briefLangKey(login)); if (v) return v; } catch {}
  const nav = ((typeof navigator !== 'undefined' && navigator.language) || '').slice(0, 2).toLowerCase();
  return BRIEF_LANGS.some(([c]) => c === nav) ? nav : 'en';
}

const BRIEF_THEN = {
  out: 'I’ve read and understood — clock off',
  in: 'I’ve read and understood — clock on',
  read: 'I’ve read and understood',
};
function BriefGate({ brief, then, remaining, busy, onAck, onBack, login }) {
  const boxRef = useRef(null);
  const [atEnd, setAtEnd] = useState(false);
  const [lang, setLangState] = useState(() => readBriefLang(login));
  const tr = lang !== 'en' && brief.i18n && brief.i18n[lang];
  const shown = tr ? { title: tr.title || brief.title, body: tr.body } : brief;
  const offered = BRIEF_LANGS.filter(([c]) => c === 'en' || (brief.i18n && brief.i18n[c]));
  const setLang = (c) => { setLangState(c); try { localStorage.setItem(briefLangKey(login), c); } catch {} };
  // Must reach the bottom before the button unlocks. A brief shorter than its box unlocks
  // straight away — there's nothing more to scroll to.
  const check = useCallback(() => {
    const el = boxRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setAtEnd(true);
  }, []);
  // A new brief, or the same one in another language, starts at the top again.
  useEffect(() => { setAtEnd(false); const el = boxRef.current; if (el) el.scrollTop = 0; const t = setTimeout(check, 50); return () => clearTimeout(t); }, [brief.id, tr, check]);
  const lead = then === 'out' ? 'Before you clock off, please read this message from the office.'
    : then === 'in' ? 'Before you clock on, please read this message from the office.'
    : 'Please read this message from the office.';
  return (
    <div style={{ ...S.card, borderColor: 'var(--hp-gold)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
        <ClipboardCheck size={20} color="var(--hp-gold)" />
        <span style={{ ...S.label, margin: 0, color: 'var(--hp-gold)' }}>Brief from the office{remaining > 1 ? ` · 1 of ${remaining}` : ''}</span>
      </div>
      {offered.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0 10px' }}>
          {offered.map(([c, name]) => (
            <button key={c} onClick={() => setLang(c)} lang={c}
              style={{ ...S.ghostBtn, padding: '6px 11px', fontSize: '0.82rem', borderColor: (tr ? lang : 'en') === c ? 'var(--hp-gold)' : 'var(--hp-border)', color: (tr ? lang : 'en') === c ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>{name}</button>
          ))}
        </div>
      )}
      <h2 style={{ ...S.sectionTitle, margin: '4px 0 6px' }} lang={tr ? lang : 'en'}>{shown.title}</h2>
      <p style={{ ...S.sub, marginBottom: 12 }}>{lead}</p>
      {tr && BRIEF_NOTE[lang] && <p style={{ ...S.sub, fontSize: '0.78rem', marginBottom: 10 }} lang={lang}>{BRIEF_NOTE[lang]}</p>}
      <div ref={boxRef} onScroll={check} data-brief-box=""
        style={{ maxHeight: '52vh', overflowY: 'auto', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '4px 14px 6px' }}>
        <div lang={tr ? lang : 'en'}><BriefText text={shown.body} /></div>
      </div>
      {!atEnd && <p style={{ ...S.sub, textAlign: 'center', margin: '10px 0 0' }}>Scroll to the end to continue.</p>}
      <button style={{ ...S.bigBtn(atEnd ? 'var(--hp-green, #22C55E)' : 'var(--hp-border)'), marginTop: 14, opacity: atEnd ? 1 : 0.6 }}
        disabled={!atEnd || busy} onClick={() => onAck(tr ? lang : 'en')}>
        {busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : <><CheckCircle2 size={18} /> {remaining > 1 ? BRIEF_THEN.read : BRIEF_THEN[then] || BRIEF_THEN.read}</>}
      </button>
      {onBack && <button style={{ ...S.ghostBtn, width: '100%', justifyContent: 'center', marginTop: 10 }} disabled={busy} onClick={onBack}>Back</button>}
    </div>
  );
}

// ── Clock tab — geofenced clock in / 30-min break countdown / clock off ───────
function ClockTab({ sorter, qrToken, qrDir }) {
  const login = sorter.login;
  const [shift, setShift] = useState(undefined); // undefined=loading · null=off-clock · obj=on shift
  const [stale, setStale] = useState(false);     // open row from a night that's already over
  const [checkFails, setCheckFails] = useState(0); // reads failed in a row — state UNKNOWN, not "off"
  const checkFailed = checkFails > 0;
  const [geo, setGeo] = useState(null);
  const [geoFails, setGeoFails] = useState(0);
  const [pending, setPending] = useState(() => readPendingTap(login)); // a tap still on its way
  const [busyState, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [, setNow] = useState(Date.now());
  const [briefs, setBriefs] = useState([]);       // unread office briefs — [] when unknown, so a failed read never holds a tap
  const [gateFor, setGateFor] = useState(null);   // 'in' | 'out' | 'read' while a brief is on screen
  const [ackBusy, setAckBusy] = useState(false);
  // A tap whose loop outlived this card (the sorter switched tabs and came back) still holds
  // the buttons, so they can't start a second one while the first is on its way.
  const busy = busyState || (pending ? pending.kind : '');
  const shiftRef = useRef(shift);
  const alertRef = useRef({ soon: false, over: false });
  useEffect(() => { shiftRef.current = shift; }, [shift]);

  function notify(title, body) { try { if ('Notification' in window && Notification.permission === 'granted') new Notification(title, { body }); } catch (e) {} }

  // The two reads are deliberately NOT awaited together. They used to share a Promise.all, so
  // the whole clock screen sat on "Loading…" until the slower of the two answered — measured
  // at 3.2s for geofence-settings, and far worse on depot wifi, which is what "the spinner
  // never finishes" looked like. The shift decides what's on screen; the geofence settings
  // only matter when a button is pressed, so let each land when it lands.
  //
  // A read that FAILED is not a read that came back "off the clock", and this used to answer
  // both of them the same way: `.catch(() => setShift(null))`. So when the request dropped at
  // 6am the card flipped to "You're off the clock" — green Clock On button and all — on a
  // sorter who was very much on it. Franco Toledo got that screen on 10 Sep 2026 and never
  // managed to clock off; Fernando Peñaloza tapped the button it offered him and opened a
  // second shift on top of his real one. Keep the last answer we actually got, and say
  // plainly that we couldn't check.
  const load = useCallback(() => {
    appsscript('getSorterActiveShift', { login })
      .then(a => {
        if (!a || !a.success) throw new Error((a && a.error) || 'unreadable');
        setShift(a.shift || null);
        // An Open row from a night that's over is NOT "on shift now" — see getSorterActiveShift.
        setStale(!!a.stale);
        setCheckFails(0);
      })
      .catch(() => setCheckFails(n => n + 1));
    loadGeo();
  }, [login]);
  // A failed geofence read is "not known yet", never "no fence set" — the old screen told the
  // crew "Depot geofence not set yet" whenever this read timed out.
  const loadGeo = useCallback(() => {
    fetch('/.netlify/functions/geofence-settings')
      .then(r => r.json())
      .then(g => { if (!g || g.success === false || !g.settings) throw new Error('unread'); setGeo(g.settings); setGeoFails(0); })
      .catch(() => setGeoFails(n => n + 1));
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadBriefs = useCallback(() => {
    withTimeout(appsscript('getMySorterBriefs', { login }), 15000, NET_TIMEOUT_MSG)
      .then(r => setBriefs(r && r.success && Array.isArray(r.briefs) ? r.briefs : []))
      .catch(() => setBriefs([]));
  }, [login]);
  useEffect(() => { loadBriefs(); }, [loadBriefs]);
  // Keep asking on the sorter's behalf rather than parking them on a "Try again" button.
  useEffect(() => {
    if (!checkFails) return undefined;
    const t = setTimeout(load, Math.min(5000 * checkFails, 20000));
    return () => clearTimeout(t);
  }, [checkFails, load]);
  useEffect(() => {
    if (!geoFails) return undefined;
    const t = setTimeout(loadGeo, Math.min(5000 * geoFails, 20000));
    return () => clearTimeout(t);
  }, [geoFails, loadGeo]);

  // 1-second tick drives the live timers and fires the break alerts once each.
  useEffect(() => {
    const t = setInterval(() => {
      setNow(Date.now());
      const s = shiftRef.current;
      const onBreak = s && s['Break Start'] && !s['Break End'];
      if (onBreak) {
        const rem = BREAK_MIN * 60 - (Date.now() - new Date(s['Break Start']).getTime()) / 1000;
        if (rem <= 300 && !alertRef.current.soon) { alertRef.current.soon = true; notify('Break finishing soon', '5 minutes left on your break.'); }
        if (rem <= 0 && !alertRef.current.over) { alertRef.current.over = true; notify('Break over', 'Clock back on now.'); }
      } else { alertRef.current = { soon: false, over: false }; }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // CLOCK-OFF IS NEVER BLOCKED HERE EITHER (18 Aug 2026 — see sorter-shift-sb.js). This
  // check used to refuse a clock-off client-side, before the server ever heard about it, so
  // the refusal wasn't even logged. The LOG OUT pin is 175m from the LOG IN pin on a 50m
  // radius: finishing at the wrong door read as "you're 177m away" and stranded the sorter
  // with an Open shift. Clocking OFF now always goes through; the server records where they
  // were and flags it for the office. A missing GPS fix doesn't stop a clock-off either.
  async function geofenced(direction) {
    if (direction === 'out') {
      try { return await withTimeout(getPosition(), 20000, GPS_TIMEOUT_MSG); }
      catch (e) { return {}; } // no location ⇒ still clock off; the server flags it
    }
    const pos = await withTimeout(getPosition(), 20000, GPS_TIMEOUT_MSG);
    const pins = geo && geo.depotPins;
    const pin = pins && pins.sorter;
    const radius = (geo && (geo.sorterRadiusM || geo.geofenceRadiusM)) || 100;
    if (geo && geo.geofenceEnabled && pin) {
      const d = haversine(pos, pin);
      // Forgive the fix's own margin, and don't enforce on a fix too vague to mean anything
      // — the same rule the server applies, so the two can't disagree.
      const acc = Math.max(0, Number(pos.acc) || 0);
      if (acc <= 200 && d - Math.min(acc, 75) > radius) throw new Error(`You're ${Math.round(d)}m from the clock-on point — it only works on site.`);
    }
    return pos;
  }

  // One attempt at a tap. `retry` = no real answer yet, worth sending the same tap again.
  async function attemptTap(p) {
    let r;
    try { r = await withTimeout(appsscript(p.action, { ...p.payload, tapAt: p.tapAt, sentAt: new Date().toISOString() }), 25000, NET_TIMEOUT_MSG); }
    catch (e) { return { retry: true, error: e.message }; }
    if (r && r.success) return { ok: true, r };
    if (!r || RETRYABLE_GATES.has(r.gate)) return { retry: true, error: (r && r.error) || NET_TIMEOUT_MSG };
    return { retry: false, error: r.error || 'That didn\'t go through.' };
  }
  // Send a tap until it gets a real answer or the window closes. Survives a tab switch (the
  // loop keeps running) and a reload (the tap is resumed from localStorage on mount).
  async function runTap(p) {
    if (activeTaps.has(p.tapAt)) return { cancelled: true };
    activeTaps.add(p.tapAt);
    const save = (q) => { writePendingTap(login, q); setPending(q); };
    try {
      for (let i = 0; ; i++) {
        if (Date.now() - Date.parse(p.tapAt) > TAP_WINDOW_MS) return { ok: false, expired: true };
        save({ ...p, attempts: i + 1 });
        const res = await attemptTap(p);
        if (!res.retry) return res;
        await pause(TAP_RETRY_STEPS_MS[Math.min(i, TAP_RETRY_STEPS_MS.length - 1)]);
      }
    } finally { activeTaps.delete(p.tapAt); save(null); }
  }
  const tapClock = (iso) => fmtTime(iso);
  const expiredText = (p) => `Your ${TAP_VERB[p.kind] || 'tap'} at ${tapClock(p.tapAt)} couldn't reach the office for 10 minutes, so it wasn't recorded. Tap again now, and tell your team leader you tapped at ${tapClock(p.tapAt)} — the office will put the right time on it.`;
  function successText(p, r) {
    if (p.kind === 'in') return `Clocked on at ${tapClock(r.clockIn || (r.shift && r.shift['Clock In']) || p.tapAt)} — have a good shift.`;
    if (p.kind === 'out') {
      if (r.alreadyOff) return `You were already clocked off at ${fmtTime(r.clockOut)}${r.workedHours != null ? ` — ${r.workedHours}h recorded` : ''}. Nothing more to do.`;
      if (r.staleClosed) return 'That shift was left open from an earlier night — it has been closed at your rostered finish and sent to the office to check.';
      return `Clocked off at ${tapClock(p.tapAt)} — ${r.workedHours}h recorded${r.autoBreak ? ' (a 30-min break was applied)' : ''}.`;
    }
    return null; // breaks: the card itself changes, no banner needed
  }
  async function completeTap(p) {
    setBusy(p.kind); setMsg(null);
    try {
      const res = await runTap(p);
      if (res.cancelled) return;
      if (res.expired) { setMsg({ ok: false, text: expiredText(p) }); return; }
      if (!res.ok) { setMsg({ ok: false, text: res.error }); return; }
      if (p.kind === 'breakStart' || p.kind === 'breakEnd') alertRef.current = { soon: false, over: false };
      const text = successText(p, res.r);
      if (text) setMsg({ ok: true, text });
    } finally { setBusy(''); load(); }
  }
  // Came back to this tab while a tap was still being sent from before: follow it, and
  // re-read the shift the moment it lands.
  useEffect(() => {
    const t = setInterval(() => {
      const p = readPendingTap(login);
      setPending(prev => {
        if ((prev && prev.tapAt) === (p && p.tapAt)) return prev;
        if (!p) load();
        return p;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [login, load]);
  // A tap still on its way when the page was closed or reloaded: pick it back up.
  useEffect(() => {
    const saved = readPendingTap(login);
    if (!saved || activeTaps.has(saved.tapAt)) return;
    if (Date.now() - Date.parse(saved.tapAt) > TAP_WINDOW_MS) { writePendingTap(login, null); setPending(null); setMsg({ ok: false, text: expiredText(saved) }); return; }
    completeTap(saved);
  }, [login]);

  async function doClockIn() {
    const tapAt = new Date().toISOString(); // the moment they tapped — before GPS, before the network
    setBusy('in'); setMsg(null);
    let pos;
    try {
      if ('Notification' in window && Notification.permission === 'default') { try { await Notification.requestPermission(); } catch (e) {} }
      pos = await geofenced('in');
    } catch (e) { setMsg({ ok: false, text: e.message }); setBusy(''); return; }
    await completeTap({ kind: 'in', action: 'sorterClockIn', tapAt, payload: { login, name: sorter.name, lat: pos.lat, lng: pos.lng, acc: pos.acc, qr: qrToken || '' } });
  }
  function simple(action, label) {
    return completeTap({ kind: label, action, tapAt: new Date().toISOString(), payload: { login } });
  }
  // The load() in completeTap's `finally` matters as much as the call itself: when the phone
  // lost the answer the old code left the card showing a shift that was already closed, so
  // the next tap came back "no open shift" in red. Every outcome re-reads the truth.
  async function doClockOut() {
    const tapAt = new Date().toISOString();
    setBusy('out'); setMsg(null);
    const pos = await geofenced('out'); // never throws for 'out'
    await completeTap({ kind: 'out', action: 'sorterClockOut', tapAt, payload: { login, lat: pos.lat, lng: pos.lng, acc: pos.acc, qr: qrToken || '' } });
  }
  // The buttons go through here: an unread brief comes first, then the real tap. The tap's
  // time is taken when it actually runs (after the brief), so reading is on the clock and
  // the 10-minute send window starts fresh.
  const tapIn = () => (briefs.length ? setGateFor('in') : doClockIn());
  const tapOut = () => (briefs.length ? setGateFor('out') : doClockOut());
  async function ackBrief(lang) {
    const b = briefs[0];
    if (!b) return;
    setAckBusy(true);
    // Saved or not, move on: a dropped signal must not keep someone from clocking off. If it
    // didn't save, the brief is still unread on the server and comes back next time.
    try { await withTimeout(appsscript('ackSorterBrief', { login, id: b.id, lang }), 15000, NET_TIMEOUT_MSG); } catch (e) {}
    setAckBusy(false);
    const rest = briefs.slice(1);
    setBriefs(rest);
    if (rest.length) return;
    const then = gateFor;
    setGateFor(null);
    if (then === 'in') doClockIn();
    else if (then === 'out') doClockOut();
  }

  if (shift === undefined && !checkFailed) return <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>;

  // A tap that has been on its way for a while: say so calmly, and say the time is safe.
  const stillTrying = pending && (Date.now() - Date.parse(pending.tapAt) > TAP_QUIET_MS);
  const banner = (
    <>
      {stillTrying && (
        <div style={{ ...S.pill('rgba(245,158,11,0.12)', 'var(--hp-amber, #F59E0B)'), padding: '10px 12px', marginBottom: 14 }}>
          <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Still reaching the office with your {TAP_VERB[pending.kind] || 'tap'}. <strong>Your tap at {fmtTime(pending.tapAt)} is the time that counts</strong> — no need to tap again. Keep this screen open.
        </div>
      )}
      {msg && (
        <div style={{ ...S.pill(msg.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', msg.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '10px 12px', marginBottom: 14 }}>
          {msg.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {msg.text}
        </div>
      )}
    </>
  );

  // Direction-locked QR on the way IN: clocking on needs the green LOG IN code (dir=in).
  // Clocking OFF is no longer gated on the red code (18 Aug 2026) — an unscanned clock-off
  // is recorded and flagged instead of refused, because a refused clock-off loses hours.
  const qrEnforced = geo && (geo.sorterQrRequired ?? geo.qrRequired) && geo.qrConfigured;
  const needQrIn = qrEnforced && qrDir !== 'in';

  if (gateFor && briefs.length) {
    return (
      <>
        {banner}
        <BriefGate brief={briefs[0]} then={gateFor} remaining={briefs.length} busy={ackBusy} login={login}
          onAck={ackBrief} onBack={() => setGateFor(null)} />
      </>
    );
  }
  const briefNote = briefs.length > 0 && (
    <div style={{ ...S.card, borderColor: 'var(--hp-gold)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <ClipboardCheck size={20} color="var(--hp-gold)" />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{briefs.length === 1 ? 'A brief from the office' : `${briefs.length} briefs from the office`}</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>{shift && !stale ? 'You’ll be asked to read it before you clock off.' : 'You’ll be asked to read it before you clock on.'}</div>
      </div>
      <button style={{ ...S.ghostBtn, borderColor: 'var(--hp-gold)', color: 'var(--hp-gold)' }} onClick={() => setGateFor('read')}>Read it now</button>
    </div>
  );

  // We have never had a straight answer about this sorter's shift. The one thing this screen
  // must not do is guess "off the clock" and offer a Clock On — guessing that way is what
  // opens a second shift on top of a running one. Say what we know and hand them the retry.
  if (shift === undefined) {
    return (
      <>
        {banner}
        <div style={{ ...S.card, borderColor: 'var(--hp-amber, #F59E0B)', textAlign: 'center', padding: '28px 20px' }}>
          <AlertTriangle size={34} color="var(--hp-amber, #F59E0B)" />
          <h2 style={{ ...S.sectionTitle, marginTop: 12 }}>Couldn't check your shift</h2>
          <p style={S.sub}>The signal dropped, so we can't tell you whether you're on the clock right now. <strong>Nothing has been lost</strong> — your shift is recorded on our side either way. We're checking again automatically.</p>
          <button style={S.bigBtn('var(--hp-gold)')} onClick={() => load()}>Check now <ChevronRight size={18} /></button>
          <p style={{ ...S.sub, marginTop: 14, marginBottom: 0 }}>Still won't load and you're finishing? Tell your team leader — the office can close the shift for you and you keep the hours.</p>
        </div>
      </>
    );
  }

  // A shift left open from an earlier night. Showing it as a running timer is what cost
  // Laura a whole shift: her Sunday night never closed, the app said "on shift" for 30
  // hours, so she never clocked on for Monday and Monday has no record at all. Say what
  // actually happened and let her start tonight — clocking on closes the old row at its
  // rostered finish and sends it to the office.
  if (shift && stale) {
    const night = shift.Date ? new Date(shift.Date + 'T00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' }) : 'an earlier night';
    return (
      <>
        {banner}
        {briefNote}
        <div style={{ ...S.card, borderColor: 'var(--hp-amber, #F59E0B)', textAlign: 'center', padding: '28px 20px' }}>
          <AlertTriangle size={34} color="var(--hp-amber, #F59E0B)" />
          <h2 style={{ ...S.sectionTitle, marginTop: 12 }}>Your {night} shift was never clocked off</h2>
          <p style={S.sub}>You started at {fmtTime(shift['Clock In'])} and no clock-off was recorded. You are <strong>not</strong> on the clock now. Clock on below for tonight — that shift will be closed at your rostered finish and sent to the office to check, so you don't lose the hours.</p>
          <button style={S.bigBtn('var(--hp-green, #22C55E)')} disabled={busy === 'in'} onClick={tapIn}>
            {busy === 'in' ? <><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Clocking on…</> : <>Clock On <ChevronRight size={18} /></>}
          </button>
        </div>
      </>
    );
  }

  if (!shift) {
    const ready = geo && geo.geofenceEnabled && geo.depotPins && geo.depotPins.sorter;
    return (
      <>
        {banner}
        {briefNote}
        <div style={{ ...S.card, textAlign: 'center', padding: '32px 20px' }}>
          <Clock size={40} color="var(--hp-gold)" />
          <h2 style={{ ...S.sectionTitle, marginTop: 12 }}>You're off the clock</h2>
          {needQrIn ? (
            <p style={S.sub}>{qrDir === 'out'
              ? 'That was the LOG OUT code. To clock on, scan the green LOG IN code at the start point.'
              : 'Scan the green LOG IN code to clock on. Open your camera at the LOG IN poster on site.'}</p>
          ) : (
            <>
              <p style={S.sub}>{ready || !geo ? 'Tap below to clock on — you must be at the depot.' : 'Tap below to clock on. (Depot geofence not set yet — your location is still recorded.)'}</p>
              <button style={S.bigBtn('var(--hp-green, #22C55E)')} disabled={busy === 'in'} onClick={tapIn}>
                {busy === 'in' ? <><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Clocking on…</> : <>Clock On <ChevronRight size={18} /></>}
              </button>
            </>
          )}
        </div>
      </>
    );
  }

  const clockInMs = new Date(shift['Clock In']).getTime();
  const onBreak = shift['Break Start'] && !shift['Break End'];
  const breakDone = shift['Break Start'] && shift['Break End'];
  const remainingMs = onBreak ? (BREAK_MIN * 60 * 1000 - (Date.now() - new Date(shift['Break Start']).getTime())) : 0;
  const overdue = remainingMs < 0;

  return (
    <>
      {banner}
      {briefNote}
      <div style={{ ...S.card, textAlign: 'center' }}>
        <div style={S.label}>On shift since {fmtTime(shift['Clock In'])}</div>
        <div style={{ fontSize: '2.4rem', fontWeight: 800, fontFamily: 'var(--font-mono, monospace)', color: 'var(--hp-text)', margin: '6px 0' }}>{fmtDur(Date.now() - clockInMs)}</div>
        {onBreak ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ ...S.label, color: overdue ? 'var(--hp-red, #EF4444)' : 'var(--hp-amber, #F59E0B)' }}>{overdue ? 'Break overdue — clock back on' : 'On break — time remaining'}</div>
            <div style={{ fontSize: '2.8rem', fontWeight: 800, fontFamily: 'var(--font-mono, monospace)', color: overdue ? 'var(--hp-red, #EF4444)' : 'var(--hp-amber, #F59E0B)' }}>{overdue ? '-' : ''}{fmtDur(Math.abs(remainingMs))}</div>
            <button style={{ ...S.bigBtn('var(--hp-gold)'), marginTop: 12 }} disabled={busy === 'breakEnd'} onClick={() => simple('sorterBreakEnd', 'breakEnd')}>{busy === 'breakEnd' ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'End break'}</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {!breakDone && <button style={{ ...S.bigBtn('var(--hp-amber, #F59E0B)'), flex: 1 }} disabled={!!busy} onClick={() => simple('sorterBreakStart', 'breakStart')}>{busy === 'breakStart' ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Start break'}</button>}
            {/* Clock off is ALWAYS available — a missing poster, a dead camera or the wrong
                door must never leave a worked shift open. Scanning the LOG OUT code is still
                the right way to do it; not scanning just flags the row for the office. */}
            <button style={{ ...S.bigBtn('var(--hp-red, #EF4444)'), flex: 1 }} disabled={busy === 'out'} onClick={tapOut}>{busy === 'out' ? <><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Clocking off…</> : 'Clock off'}</button>
          </div>
        )}
        {breakDone && !onBreak && <div style={{ ...S.pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)'), marginTop: 12 }}><CheckCircle2 size={13} /> 30-min break taken ({fmtTime(shift['Break Start'])}–{fmtTime(shift['Break End'])})</div>}
        {/* The last check didn't get through, so this card is the last known state, not a live
            one. Better to show a shift that may already be closed — tapping Clock off again
            just answers "you were already clocked off" — than to hide the button. */}
        {checkFailed && <div style={{ ...S.pill('rgba(245,158,11,0.12)', 'var(--hp-amber, #F59E0B)'), marginTop: 12 }}><AlertTriangle size={13} /> Couldn't refresh just now — this is your last known status. Clock off still works.</div>}
      </div>
      <p style={{ ...S.sub, textAlign: 'center' }}>A 30-minute unpaid break is applied automatically to every shift — you don't need to record it. The break timer is optional if you'd like a countdown. Clock on/off records your location.</p>
    </>
  );
}

// ── My History tab ────────────────────────────────────────────────────────────
// ── My hours — check the period's shifts, confirm them or tell us one is wrong ─
//
// WHY: hours used to go to the pay run on the office's reading of the clock alone, and a
// wrong shift only surfaced after the money moved. The sorter is the one person who knows
// what they actually worked, so they get asked BEFORE the run. Silence never blocks pay
// (you must pay someone whether or not they tapped a button) — it just leaves the shift
// unconfirmed, and an open dispute is what actually holds a line back.

// The illustrated manual (public/check-my-hours.html, allowlisted in the edge
// password-gate). A new tab rather than a route: it is the same standalone-guide pattern
// the safety topics use, and a sorter half-way through checking a period should not lose
// the list to read the instructions. Shown on the shift page AND on My Shifts, which is
// the first tab a sorter meets — the guide is no use to someone who never finds it.
function HoursGuideLink({ style }) {
  return (
    <a href="/check-my-hours.html" target="_blank" rel="noreferrer"
      style={{ ...S.ghostBtn, width: '100%', justifyContent: 'center', textDecoration: 'none',
        marginBottom: 14, color: 'var(--hp-gold)', borderColor: 'var(--hp-gold)', ...style }}>
      <BookOpen size={15} /> How to check your hours
    </a>
  );
}

const REVIEW_LOOK = {
  awaiting:  { label: 'Needs checking',  colour: 'var(--hp-amber, #F59E0B)', bg: 'rgba(245,158,11,0.12)' },
  changed:   { label: 'Changed — check again', colour: 'var(--hp-amber, #F59E0B)', bg: 'rgba(245,158,11,0.12)' },
  confirmed: { label: 'You confirmed this', colour: 'var(--hp-green, #22C55E)', bg: 'rgba(34,197,94,0.12)' },
  disputed:  { label: 'You told us it’s wrong', colour: 'var(--hp-red, #EF4444)', bg: 'rgba(239,68,68,0.12)' },
  resolved:  { label: 'The office answered', colour: 'var(--hp-gold)', bg: 'rgba(212,175,55,0.12)' },
};

const dayLabel = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return iso; } };
const hoursLabel = (h) => (h == null || h === '' ? '—' : Number(h).toFixed(2).replace(/\.00$/, '') + 'h');
const shortDay = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); } catch { return iso; } };
const periodLabel = (p) => `${shortDay(p.from)} – ${shortDay(p.to)}`;

// ── The period switcher ───────────────────────────────────────────────────────
// A pay period closes on a Saturday night and the office builds the pay days later, so
// the days when someone most needs to check a period all fall AFTER it has ended. The tab
// used to show only the period containing today, which meant a closed period vanished off
// the phone at midnight while the office was still chasing it. These are the periods they
// can still answer for, with what is still unchecked in each.
function PeriodSwitch({ periods, selected, onPick, busy }) {
  if (!periods || periods.length < 2) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
      {periods.map(p => {
        const on = p.from === selected;
        return (
          <button key={p.from} disabled={busy} onClick={() => onPick(p.current ? '' : p.from)}
            style={{ ...S.ghostBtn, padding: '8px 12px', fontSize: '0.78rem', opacity: busy ? 0.6 : 1,
              borderColor: on ? 'var(--hp-gold)' : 'var(--hp-border)', color: on ? 'var(--hp-gold)' : 'var(--hp-dim)',
              fontWeight: on ? 800 : 600 }}>
            {p.current ? 'This period' : periodLabel(p)}
            {p.awaiting > 0 && (
              <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 800,
                background: 'rgba(245,158,11,0.16)', color: 'var(--hp-amber, #F59E0B)' }}>{p.awaiting}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function HistoryTab({ sorter, review, reload, switching, onPeriod }) {
  const [older, setOlder] = useState(null);
  const [showOlder, setShowOlder] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [disputeId, setDisputeId] = useState(null);
  const [note, setNote] = useState('');
  const [sheetFor, setSheetFor] = useState(null); // shift date whose paper sheet is open

  useEffect(() => {
    if (!showOlder || older !== null) return;
    appsscript('getSorterTimesheets', { login: sorter.login, limit: 40 }).then(r => setOlder(r && r.success ? r.timesheets : [])).catch(() => setOlder([]));
  }, [showOlder, older, sorter.login]);

  async function act(action, id, extra) {
    setBusy(id); setErr('');
    try {
      const r = await appsscript(action, { login: sorter.login, ...(id === 'all' ? {} : { id }), ...extra });
      if (!r || !r.success) throw new Error((r && r.error) || 'That didn’t save — try again.');
      setDisputeId(null); setNote('');
      await reload();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  if (!review) return <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>;
  const { period, shifts, counts, periods, prior } = review;
  const periodDates = period ? `${dayLabel(period.from)} – ${dayLabel(period.to)}` : '';
  const isCurrent = !periods || !period || (periods.find(p => p.from === period.from) || {}).current;

  return (
    <>
      <div style={S.card}>
        <h2 style={S.sectionTitle}>My hours</h2>
        <p style={S.sub}>
          {isCurrent ? 'This pay period' : 'Finished pay period'} · {periodDates}. A shift appears here once the office has approved the hours — check it and confirm it, or tell us if it&apos;s wrong, before we build your pay.
          {!isCurrent && ' This period has ended, so it goes to the pay run next — now is the time to tell us.'}
        </p>

        <HoursGuideLink />

        <PeriodSwitch periods={periods} selected={period && period.from} onPick={onPeriod} busy={switching} />

        {/* A finished period nobody has answered for is the urgent one — it is the pay
            about to be built — and it is the one nobody would think to go looking for. */}
        {prior && (
          <button onClick={() => onPeriod(prior.from)} disabled={switching}
            style={{ ...S.ghostBtn, width: '100%', justifyContent: 'center', marginBottom: 14,
              borderColor: 'var(--hp-amber, #F59E0B)', color: 'var(--hp-amber, #F59E0B)' }}>
            <AlertTriangle size={14} /> {prior.awaiting} shift{prior.awaiting === 1 ? '' : 's'} still to check from {periodLabel(prior)}
          </button>
        )}

        {!!counts.total && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            <span style={S.pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={12} /> {counts.confirmed} confirmed</span>
            {counts.awaiting > 0 && <span style={S.pill('rgba(245,158,11,0.12)', 'var(--hp-amber, #F59E0B)')}>{counts.awaiting} to check</span>}
            {counts.disputed > 0 && <span style={S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')}><AlertTriangle size={12} /> {counts.disputed} reported</span>}
          </div>
        )}

        {err && <div style={{ ...S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), marginBottom: 12 }}><AlertTriangle size={13} />{err}</div>}

        {/* The end-of-period sign-off in one tap. Paul's ask was "select that everything is
            good" — twelve separate taps is not that. Disputes are never swept up by it. */}
        {counts.awaiting > 1 && (
          <button style={{ ...S.bigBtn('var(--hp-green, #22C55E)'), marginBottom: 14 }} disabled={busy === 'all'}
            onClick={() => act('confirmSorterPeriod', 'all', { from: period.from, to: period.to })}>
            {busy === 'all' ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : <><CheckCircle2 size={18} /> All {counts.awaiting} of these are right</>}
          </button>
        )}

        {!shifts.length && !counts.pending && <p style={{ ...S.sub, marginBottom: 0 }}>{isCurrent ? 'No shifts recorded in this period yet.' : 'You had no shifts in that period.'}</p>}

        {/* Shifts the office hasn't approved yet are deliberately NOT listed — the times
            are still being worked on and signing off a figure that is about to change
            helps nobody. But an empty list with no explanation reads as "they've lost my
            shifts", so we say how many are still on our side of the desk. */}
        {!!counts.pending && (
          <p style={{ ...S.sub, marginBottom: 0, borderTop: shifts.length ? '1px solid var(--hp-border)' : 'none', paddingTop: shifts.length ? 12 : 0 }}>
            <Clock size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
            {counts.pending} more {counts.pending === 1 ? 'night' : 'nights'} {counts.pending === 1 ? 'is' : 'are'} still being checked by the office. {counts.pending === 1 ? 'It' : 'They'} will appear here to check as soon as the hours are approved — nothing is paid before that.
          </p>
        )}

        {shifts.map(sh => {
          const look = REVIEW_LOOK[sh.state] || REVIEW_LOOK.awaiting;
          const open = disputeId === sh.id;
          const answerable = sh.state === 'awaiting' || sh.state === 'changed';
          return (
            <div key={sh.id} style={{ padding: '12px 0', borderTop: '1px solid var(--hp-border)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{dayLabel(sh.date)}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
                    {fmtTime(sh.clockIn)} – {sh.clockOut ? fmtTime(sh.clockOut) : 'not clocked off'}
                    {sh.breakMins ? ` · ${sh.breakMins}m break` : ''}
                  </div>
                </div>
                <div style={{ fontWeight: 800, fontFamily: 'var(--font-mono, monospace)' }}>{hoursLabel(sh.hours)}</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={S.pill(look.bg, look.colour)}>{look.label}</span>
                {sh.status !== 'Approved' && <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>waiting on the office</span>}
                {sh.hasSheet && (
                  <button style={{ ...S.ghostBtn, padding: '6px 10px', fontSize: '0.76rem' }} onClick={() => setSheetFor(sheetFor === sh.date ? null : sh.date)}>
                    <FileText size={13} /> {sheetFor === sh.date ? 'Hide' : 'Sign-in sheet'}
                  </button>
                )}
              </div>

              {sh.state === 'changed' && (
                <p style={{ ...S.sub, margin: '8px 0 0' }}>The office changed this shift after you confirmed it. Have another look and confirm the new hours.</p>
              )}
              {sh.state === 'disputed' && (
                <p style={{ ...S.sub, margin: '8px 0 0' }}>You said: “{sh.disputeNote}” — we&apos;re looking at it — you don&apos;t need to do anything else.</p>
              )}
              {sh.state === 'resolved' && (
                <p style={{ ...S.sub, margin: '8px 0 0' }}>You said: “{sh.disputeNote}” — the office replied: “{sh.resolution || 'sorted'}”. If it&apos;s still not right, tell us again.</p>
              )}

              {sheetFor === sh.date && <SignSheetPanel date={sh.date} />}

              {/* "Something's wrong" stays available on a shift they already confirmed —
                  someone who ticks twelve shifts and then notices one is short must have a
                  way back. Only an open dispute hides it (it's already with the office). */}
              {sh.state !== 'disputed' && !open && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {(answerable || sh.state === 'resolved') && (
                    <button style={{ ...S.bigBtn('var(--hp-green, #22C55E)'), flex: 1, minWidth: 150, padding: '11px 14px' }}
                      disabled={busy === sh.id} onClick={() => act('confirmSorterShift', sh.id)}>
                      {busy === sh.id ? <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> : <><CheckCircle2 size={16} /> This is right</>}
                    </button>
                  )}
                  <button style={{ ...S.ghostBtn, flex: 1, minWidth: 150, justifyContent: 'center' }} onClick={() => { setDisputeId(sh.id); setNote(''); }}>
                    <Flag size={14} /> {sh.state === 'confirmed' ? 'Actually, it\u2019s wrong' : 'Something\u2019s wrong'}
                  </button>
                </div>
              )}

              {open && (
                <div style={{ marginTop: 10 }}>
                  <label style={S.label}>What&apos;s wrong with this shift?</label>
                  <textarea style={{ ...S.input, minHeight: 84, resize: 'vertical' }} value={note} autoFocus
                    onChange={e => setNote(e.target.value)}
                    placeholder="e.g. I started at 23:15, not 23:45 — I signed the paper sheet at 23:15." />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...S.bigBtn('var(--hp-gold)'), flex: 1, padding: '11px 14px' }}
                      disabled={!note.trim() || busy === sh.id} onClick={() => act('disputeSorterShift', sh.id, { note })}>
                      {busy === sh.id ? <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> : <><Send size={15} /> Send to the office</>}
                    </button>
                    <button style={S.ghostBtn} onClick={() => { setDisputeId(null); setNote(''); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={S.card}>
        <button onClick={() => setShowOlder(v => !v)} style={{ background: 'transparent', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.85rem', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <History size={14} /> {showOlder ? 'Hide' : 'Show'} earlier shifts
        </button>
        {showOlder && (older === null
          ? <div style={{ marginTop: 12, color: 'var(--hp-dim)' }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
          : (
            <div style={{ marginTop: 12 }}>
              {!older.length && <p style={{ ...S.sub, marginBottom: 0 }}>No earlier shifts.</p>}
              {older.map((r, i) => (
                <div key={r.ID || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: '1px solid var(--hp-border)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r['Clock In'] ? new Date(r['Clock In']).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : r.Date}</div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>{fmtTime(r['Clock In'])} – {r['Clock Out'] ? fmtTime(r['Clock Out']) : 'in progress'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontFamily: 'var(--font-mono, monospace)' }}>{hoursLabel(r['Worked Hours'])}</div>
                    <div style={{ fontSize: '0.72rem', color: r.Status === 'Approved' ? 'var(--hp-green, #22C55E)' : 'var(--hp-dim)' }}>{r.Status}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
    </>
  );
}

// The AP paper sign-in sheet for one night — the photo taken at the depot, plus the line
// we read off it for this sorter. Their own handwritten in/out is the only thing that
// actually settles "my hours are wrong", so it sits next to the shift they're checking.
// The whole page shows: they sign it alongside everyone else in the same room, so a crop
// would buy no privacy the paper doesn't already give away.
function SignSheetPanel({ date }) {
  const [state, setState] = useState(null); // null = loading · {sheet} · {error}
  useEffect(() => {
    let alive = true;
    setState(null);
    fetch('/.netlify/functions/ap-signsheet-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'apSheetForSorter', shiftDate: date }),
    }).then(r => r.json().then(d => checkSessionExpired(r.status, d)))
      .then(d => { if (alive) setState(d && d.success ? { sheet: d.sheet } : { error: (d && d.error) || 'Could not open the sheet.' }); })
      .catch(() => { if (alive) setState({ error: 'Could not open the sheet.' }); });
    return () => { alive = false; };
  }, [date]);

  if (state === null) return <div style={{ marginTop: 10, color: 'var(--hp-dim)', fontSize: '0.8rem' }}><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Opening the sheet…</div>;
  if (state.error) return <div style={{ ...S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), marginTop: 10 }}><AlertTriangle size={13} />{state.error}</div>;
  if (!state.sheet) return <p style={{ ...S.sub, margin: '10px 0 0' }}>No sign-in sheet was photographed for that night.</p>;

  const { viewUrl, mine } = state.sheet;
  return (
    <div style={{ marginTop: 10, padding: 12, border: '1px solid var(--hp-border)', borderRadius: 8, background: 'var(--hp-card, #1E1E24)' }}>
      {mine
        ? <p style={{ fontSize: '0.8rem', margin: '0 0 8px', lineHeight: 1.5 }}>
            On the paper you signed in at <strong>{mine.signInRaw || '—'}</strong> and out at <strong>{mine.signOutRaw || '—'}</strong>.
            {' '}<span style={{ color: 'var(--hp-dim)' }}>That&apos;s our reading of the handwriting — check it against the photo.</span>
          </p>
        : <p style={{ fontSize: '0.8rem', margin: '0 0 8px', color: 'var(--hp-dim)' }}>We couldn&apos;t match your name on this sheet. Have a look — if you signed it, tell us.</p>}
      {viewUrl && <a href={viewUrl} target="_blank" rel="noreferrer"><img src={viewUrl} alt={`AP sign-in sheet for ${date}`} style={{ width: '100%', borderRadius: 6, display: 'block' }} /></a>}
      <p style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', margin: '8px 0 0' }}>Tap the photo to open it full size.</p>
    </div>
  );
}

// ── "Check your hours before we run the pay" banner ───────────────────────────
// Fires only once the sorter's last rostered night of the period has passed and something
// is still unanswered — a nag on every shift would train them to ignore it.
function ReviewBanner({ review, onOpen }) {
  if (!review) return null;
  // A CLOSED period with unchecked shifts outranks the current one: that pay is the one
  // about to be built, and the shifts in it are the ones nobody would go looking for.
  const prior = review.prior;
  if (!prior && !review.promptDue) return null;
  const n = prior ? prior.awaiting : review.counts.awaiting;
  return (
    <div style={{ ...S.card, borderColor: 'var(--hp-gold)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <CheckCircle2 size={20} color="var(--hp-gold)" />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>
          {prior ? `Your ${periodLabel(prior)} pay period is finished` : 'That\u2019s your last shift of the period'}
        </div>
        <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>Check your {n} unchecked shift{n === 1 ? '' : 's'} before we build your pay. If one is wrong, tell us now — it&apos;s much easier to fix before the money goes out.</div>
      </div>
      <button style={S.bigBtn('var(--hp-gold)')} onClick={() => onOpen(prior ? prior.from : '')}>Check my hours</button>
    </div>
  );
}

// ── Roster-change banner — "your shift changed", told to the person it changed for ──
// Publishing the grid used to be silent: the week is rewritten and the new picture just
// appears, so someone taken off a night only found out if the office rang them. The
// publish now diffs itself and leaves a notice here, which stays up until they tap Got it
// — and the office can see who has actually seen it (SortersAdminPage, Roster changes).
//
// Sits above every tab, not just My Shifts: a change to tonight is the one thing worth
// interrupting whatever they opened the portal to do.
function RosterChangeBanner({ login }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    let alive = true;
    appsscript('listSorterNotices', { login })
      .then(r => { if (alive && r && r.success) setItems(r.notices || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [login]);

  async function ack(n) {
    setBusy(n.id);
    // Clear it locally either way — a sorter who has read the change should not be stuck
    // staring at the banner because the network dropped. It reappears on the next load if
    // the write really did fail, which is the safe direction.
    try { await appsscript('ackSorterNotice', { login, id: n.id }); } catch {}
    setItems(list => list.filter(x => x.id !== n.id));
    setBusy('');
  }

  if (!items.length) return null;
  return (
    <div style={{ ...S.card, borderColor: 'var(--hp-gold)', padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
        <CalendarDays size={18} color="var(--hp-gold)" />
        <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>{items.length === 1 ? 'Your roster changed' : `Your roster changed (${items.length})`}</span>
      </div>
      {items.map(n => (
        <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '9px 0', borderTop: '1px solid var(--hp-border)' }}>
          <div style={{ flex: 1, minWidth: 200, fontSize: '0.86rem', lineHeight: 1.45 }}>{n.text}</div>
          <button style={{ ...S.ghostBtn, borderColor: 'var(--hp-gold)', color: 'var(--hp-gold)', fontSize: '0.78rem' }}
            disabled={busy === n.id} onClick={() => ack(n)}>
            {busy === n.id ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <><CheckCircle2 size={13} /> Got it</>}
          </button>
        </div>
      ))}
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.74rem', margin: '10px 0 0' }}>Tap Got it so the office knows you have seen it. The full week is on the Team Roster tab.</p>
    </div>
  );
}

// ── Finish-time banner — AP's morning message, pushed by the office ───────────
// The shift night is yesterday until midday (overnight shifts end in the morning),
// then today — the same rule the admin card uses.
// Local-time date string — toISOString() is UTC and knocks Sydney dates back a day.
const isoLocal = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
function currentShiftDate() {
  const d = new Date();
  if (d.getHours() < 12) d.setDate(d.getDate() - 1);
  return isoLocal(d);
}
function FinishTimeBanner() {
  const [finish, setFinish] = useState(null);
  useEffect(() => {
    appsscript('nsGetFinishTime', { shiftDate: currentShiftDate() })
      .then(r => setFinish(r && r.success ? r.finish : null))
      .catch(() => setFinish(null));
  }, []);
  if (!finish) return null;
  return (
    <div style={{ ...S.card, borderColor: 'var(--hp-gold)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <Clock size={20} color="var(--hp-gold)" />
      <div>
        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Shift finish time: {finish.finishTime}</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>Confirmed by Australia Post{finish.note ? ` · ${finish.note}` : ''}</div>
      </div>
    </div>
  );
}

// ── "The office needs your paperwork" banner ─────────────────────────────────
// Shows only once the office has actually asked (requestOnboarding) and the item is
// still missing, so it's a live request rather than a permanent nag. Reads booleans
// only — no vault unlock needed to know that something is outstanding.
// "a, b and c" — three things joined by 'and' twice reads badly.
const listPhrase = (a) => (a.length < 2 ? (a[0] || '') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]);

function TaxSuperBanner({ login, onOpenDocs }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    vaultFetch({ op: 'onboardingStatus', login })
      .then(d => { if (alive && d && d.success) setState(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [login]);
  if (!state || !state.request) return null;
  const want = [];
  if (!state.tfn?.has) want.push('signed TFN declaration');
  // Bank and super come from Xero; until they've been read back, a blank isn't a request.
  // A new starter's super FORM is different — Xero can't supply a signed form.
  if ((state.formsOnly || !state.payrollHeld) && !state.superan?.has) want.push('signed super choice form');
  if (!state.payrollHeld && !state.bank?.has) want.push('bank account details');
  if (!want.length) return null;
  return (
    <div style={{ ...S.card, borderColor: 'var(--hp-amber, #F59E0B)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <AlertTriangle size={20} color="var(--hp-amber, #F59E0B)" />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>The office needs your {listPhrase(want)}</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>We can't run your pay without it. Documents shows you how to get each form and where to upload it — your details are encrypted.</div>
      </div>
      <button style={S.bigBtn('var(--hp-gold)')} onClick={onOpenDocs}>Complete it now</button>
    </div>
  );
}

// ── Sort Guide — street → chute/outlet/bay lookup + full route tables ─────────
// Read-only reference so a sorter can find where a parcel goes. Same data as the
// Driver Routes page (driver_routes via nsGetSortGuide), so a reassignment shows here too.
const AREA_LABELS = { CITY: 'City', DARL: 'Darlinghurst', BWAY: 'Broadway' };
const AREA_ORDER = ['CITY', 'DARL', 'BWAY'];
const guideS = {
  chute: { background: 'rgba(245,158,11,0.14)', color: 'var(--hp-gold)' },
  outlet: { background: 'rgba(56,189,248,0.14)', color: 'var(--hp-sky, #38BDF8)' },
  bay: { background: 'rgba(236,72,153,0.14)', color: 'var(--hp-pink, #EC4899)' },
  none: { background: 'rgba(148,163,184,0.14)', color: 'var(--hp-dim)' },
};
function sortByBay(a, b) {
  const na = parseInt(a.bay, 10), nb = parseInt(b.bay, 10);
  if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
  return (a.bay || a.outlet || '').localeCompare(b.bay || b.outlet || '');
}
function RoutePills({ rt, size = '0.72rem' }) {
  const pill = (st, txt) => <span style={{ ...S.pill(st.background, st.color), fontSize: size }}>{txt}</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {pill(rt.chute ? guideS.chute : guideS.none, `Chute ${rt.chute || '—'}`)}
      {pill(rt.outlet ? guideS.outlet : guideS.none, `Outlet ${rt.outlet || '—'}`)}
      {pill(rt.bay ? guideS.bay : guideS.none, `Bay ${rt.bay || '—'}`)}
    </div>
  );
}

function SortGuideTab({ sorter }) {
  const [routes, setRoutes] = useState(null); // null = loading
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [openArea, setOpenArea] = useState({});

  useEffect(() => {
    let live = true;
    appsscript('nsGetSortGuide', {})
      .then(r => { if (!live) return; if (r && r.success) setRoutes(r.routes || []); else { setErr((r && r.error) || 'Could not load the sort guide.'); setRoutes([]); } })
      .catch(() => { if (live) { setErr('Could not load the sort guide.'); setRoutes([]); } });
    return () => { live = false; };
  }, []);

  // Flatten every street into a searchable index (a street may appear on a route
  // more than once to carry a number range — de-dupe by street+route for search).
  const index = useMemo(() => {
    const seen = new Set(); const out = [];
    (routes || []).forEach(rt => (rt.streets || []).forEach(s => {
      const label = String(s || '').trim(); if (!label) return;
      const key = rt.login + '|' + label.toLowerCase();
      if (seen.has(key)) return; seen.add(key);
      out.push({ street: label, rt });
    }));
    return out;
  }, [routes]);

  const query = q.trim().toLowerCase();
  const matches = useMemo(() => {
    if (query.length < 2) return [];
    const starts = [], contains = [];
    for (const x of index) {
      const s = x.street.toLowerCase();
      if (s.startsWith(query)) starts.push(x);
      else if (s.includes(query)) contains.push(x);
    }
    return [...starts, ...contains].slice(0, 80);
  }, [index, query]);

  // Group routes by area for the browse tables.
  const byArea = useMemo(() => {
    const g = {};
    (routes || []).forEach(rt => { const a = (rt.area || '—').toUpperCase(); (g[a] = g[a] || []).push(rt); });
    Object.values(g).forEach(list => list.sort(sortByBay));
    const known = AREA_ORDER.filter(a => g[a]);
    const rest = Object.keys(g).filter(a => !AREA_ORDER.includes(a)).sort();
    return [...known, ...rest].map(a => [a, g[a]]);
  }, [routes]);

  if (routes === null) return <div style={{ ...S.card, textAlign: 'center', padding: '40px 20px' }}><Loader2 size={22} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--hp-dim)' }} /></div>;

  const totalStreets = index.length;

  return (
    <>
      <div style={S.card}>
        <h2 style={S.sectionTitle}>Sort Guide</h2>
        <p style={S.sub}>Look up a street to see which <strong>chute</strong> and <strong>outlet</strong> it sorts to. {totalStreets} streets across {routes.length} rounds.</p>
        <div style={{ position: 'relative', marginBottom: 4 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
          <input style={{ ...S.input, paddingLeft: 36, marginBottom: 0 }} value={q} autoCapitalize="none"
            onChange={e => setQ(e.target.value)} placeholder="Search a street, e.g. Avon" />
        </div>
        {err && <div style={{ ...S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), marginTop: 12 }}><AlertTriangle size={13} />{err}</div>}
      </div>

      {query.length >= 2 && (
        <div style={S.card}>
          <h2 style={{ ...S.sectionTitle, fontSize: '1rem' }}>{matches.length ? `${matches.length}${matches.length === 80 ? '+' : ''} match${matches.length === 1 ? '' : 'es'}` : 'No matching street'}</h2>
          {matches.length === 0 && <p style={S.sub}>Nothing found for “{q}”. Check the spelling, or browse by area below.</p>}
          {matches.map((m, i) => (
            <div key={i} style={{ padding: '11px 0', borderTop: i ? '1px solid var(--hp-border)' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{m.street}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{AREA_LABELS[m.rt.area] || m.rt.area}{m.rt.dNumber ? ` · ${m.rt.dNumber}` : ''} · {m.rt.login}</span>
              </div>
              <RoutePills rt={m.rt} />
              <ReportWrongOutlet street={m.street} rt={m.rt} sorter={sorter} />
            </div>
          ))}
        </div>
      )}

      {query.length < 2 && byArea.map(([area, list]) => {
        const open = !!openArea[area];
        return (
          <div key={area} style={S.card}>
            <button onClick={() => setOpenArea(o => ({ ...o, [area]: !o[area] }))}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', color: 'var(--hp-text)', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-body)' }}>
              <MapPin size={16} color="var(--hp-gold)" />
              <span style={{ ...S.sectionTitle, fontSize: '1.05rem', flex: 1, textAlign: 'left' }}>{AREA_LABELS[area] || area}</span>
              <span style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>{list.length} rounds</span>
              <ChevronDown size={18} color="var(--hp-dim)" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {open && list.map((rt, i) => <RouteCard key={rt.login + i} rt={rt} />)}
          </div>
        );
      })}
    </>
  );
}

function RouteCard({ rt }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <RoutePills rt={rt} size="0.74rem" />
        <span style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>{rt.dNumber ? `${rt.dNumber} · ` : ''}{rt.login}</span>
      </div>
      <button onClick={() => setOpen(o => !o)} style={{ ...S.ghostBtn, padding: '6px 10px', fontSize: '0.76rem' }}>
        {open ? 'Hide' : 'Show'} {rt.streets.length} streets <ChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {rt.streets.map((s, i) => (
            <span key={i} style={{ fontSize: '0.78rem', color: 'var(--hp-text)', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '4px 8px' }}>{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// Report that the machine sent a street's parcel to the wrong outlet (programming error).
// Sends an alert to the office; expected chute/outlet come from the guide, the sorter keys
// in where the machine actually dropped it.
function ReportWrongOutlet({ street, rt, sorter }) {
  const [open, setOpen] = useState(false);
  const [actual, setActual] = useState('');
  const [note, setNote] = useState('');
  const [state, setState] = useState(''); // '' | 'sending' | 'sent' | 'error'

  async function send() {
    const a = actual.trim();
    if (!a) return;
    setState('sending');
    try {
      const r = await appsscript('nsReportMisroute', {
        street, area: rt.area, roundLogin: rt.login,
        expectedOutlet: rt.outlet, expectedChute: rt.chute, expectedBay: rt.bay,
        actualOutlet: a, note: note.trim(),
        sorterLogin: sorter?.login || '', sorterName: sorter?.name || '',
      });
      setState(r && r.success ? 'sent' : 'error');
    } catch { setState('error'); }
  }

  if (state === 'sent') return (
    <div style={{ ...S.pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)'), marginTop: 8, padding: '6px 10px' }}>
      <CheckCircle2 size={13} /> Reported to the office — thank you.
    </div>
  );

  if (!open) return (
    <button onClick={() => setOpen(true)}
      style={{ marginTop: 8, background: 'transparent', border: '1px solid var(--hp-border)', color: 'var(--hp-dim)', borderRadius: 7, padding: '5px 10px', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' }}>
      <Flag size={12} /> Machine sent it elsewhere?
    </button>
  );

  return (
    <div style={{ marginTop: 10, background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 8, lineHeight: 1.5 }}>
        The guide says <strong style={{ color: 'var(--hp-gold)' }}>Outlet {rt.outlet || '—'}</strong> (Chute {rt.chute || '—'}) for <strong style={{ color: 'var(--hp-text)' }}>{street}</strong>. Where did the machine actually send it?
      </div>
      <label style={S.label}>Machine outlet</label>
      <input style={{ ...S.input, marginBottom: 10 }} inputMode="numeric" value={actual}
        onChange={e => setActual(e.target.value)} placeholder="e.g. 20" autoFocus />
      <label style={S.label}>Note (optional)</label>
      <input style={{ ...S.input, marginBottom: 10 }} value={note}
        onChange={e => setNote(e.target.value)} placeholder="Anything the office should know" />
      {state === 'error' && <div style={{ ...S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), marginBottom: 10, padding: '6px 10px' }}><AlertTriangle size={12} /> Couldn't send — try again.</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={send} disabled={!actual.trim() || state === 'sending'}
          style={{ ...S.bigBtn('var(--hp-gold)'), width: 'auto', flex: 1, padding: '10px', fontSize: '0.85rem', opacity: (!actual.trim() || state === 'sending') ? 0.5 : 1 }}>
          {state === 'sending' ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <><Send size={14} /> Send alert</>}
        </button>
        <button onClick={() => { setOpen(false); setActual(''); setNote(''); setState(''); }}
          style={{ ...S.ghostBtn, padding: '10px 14px' }}>Cancel</button>
      </div>
    </div>
  );
}

// ── My Shifts tab — upcoming shifts + "can't work" offers ─────────────────────
function MyShiftsTab({ sorter }) {
  const [rows, setRows] = useState(null);
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 5000); };

  const load = useCallback(() => {
    const today = isoLocal(new Date());
    appsscript('getSorterRoster', { login: sorter.login, from: today })
      .then(r => setRows(r && r.success ? (r.roster || []).slice().sort((a, b) => (a.Date > b.Date ? 1 : -1)) : []))
      .catch(() => setRows([]));
    appsscript('listSorterShiftOffers', { login: sorter.login })
      .then(r => setOffers(r && r.success ? r.offers : []))
      .catch(() => setOffers([]));
  }, [sorter.login]);
  useEffect(() => { load(); }, [load]);

  async function offerShift(r) {
    const reason = window.prompt("Tell the office why you can't work this shift (optional):") ;
    if (reason === null) return;
    setBusy(r.Date + r.Slot);
    try {
      const res = await appsscript('offerSorterShift', { login: sorter.login, date: r.Date, slot: r.Slot, reason });
      if (!res.success) throw new Error(res.error || 'Could not send.');
      flash('Sent — the office will reassign or come back to you. The shift is still yours until they accept.');
      load();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function withdraw(offer) {
    setBusy('w' + offer.id);
    try {
      const res = await appsscript('withdrawSorterShiftOffer', { login: sorter.login, id: offer.id });
      if (!res.success) throw new Error(res.error || 'Could not withdraw.');
      flash('Withdrawn — the shift stays with you.');
      load();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  if (rows === null) return <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>;
  const openFor = (r) => offers.find(o => o.status === 'Open' && o.date === r.Date && o.slot === r.Slot);
  const recent = offers.filter(o => o.status !== 'Open').slice(-5).reverse();

  return (
    <>
      {msg && <div style={{ ...S.pill(msg.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', msg.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '10px 12px', marginBottom: 14 }}>{msg.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {msg.text}</div>}
      {/* Outside the rows branch on purpose: a sorter with nothing rostered yet still
          needs to be able to read how their hours get checked. */}
      <HoursGuideLink />
      {!rows.length ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '40px 20px' }}><CalendarDays size={28} color="var(--hp-dim)" /><p style={S.sub}>No upcoming shifts rostered yet.</p></div>
      ) : (
        <div style={S.card}>
          <h2 style={S.sectionTitle}>My shifts</h2>
          <p style={S.sub}>Your upcoming rostered shifts. If you can't make one, offer it back and the office will sort a swap.</p>
          {rows.map((r, i) => {
            const d = new Date(r.Date + 'T00:00');
            const offer = openFor(r);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid var(--hp-border)' }}>
                <div style={{ textAlign: 'center', minWidth: 50 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-gold)', lineHeight: 1 }}>{d.getDate()}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase' }}>{d.toLocaleDateString(undefined, { weekday: 'short', month: 'short' })}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{r.Slot}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{r.Start}–{r.End}</div>
                  {offer && <span style={{ ...S.pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)'), marginTop: 4 }}><AlertTriangle size={11} /> Offered — waiting on the office</span>}
                </div>
                {offer ? (
                  <button style={{ ...S.ghostBtn, fontSize: '0.76rem' }} disabled={busy === 'w' + offer.id} onClick={() => withdraw(offer)}>
                    {busy === 'w' + offer.id ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Keep shift'}
                  </button>
                ) : (
                  <button style={{ ...S.ghostBtn, fontSize: '0.76rem' }} disabled={busy === r.Date + r.Slot} onClick={() => offerShift(r)}>
                    {busy === r.Date + r.Slot ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : "Can't work"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {recent.length > 0 && (
        <div style={S.card}>
          <h2 style={S.sectionTitle}>Offer history</h2>
          {recent.map(o => (
            <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--hp-border)', fontSize: '0.84rem' }}>
              <span>{o.date} · {o.slot}</span>
              <span style={{ color: o.status === 'Accepted' ? 'var(--hp-green, #22C55E)' : o.status === 'Declined' ? 'var(--hp-red, #EF4444)' : 'var(--hp-dim)', fontWeight: 700 }}>
                {o.status === 'Accepted' ? (o.takenBy ? 'Covered' : 'Released') : o.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── My Payslips ───────────────────────────────────────────────────────────────
const money = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Sydney's today. A pay run is often processed days before the money moves, and "paid"
// before it lands sends someone looking in their account for nothing.
const todayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });

// The detailed statement, built on the phone from the same figures this screen shows.
// A sorter who thinks they are short needs the twenty nights, not the one total — and
// they need it as a file they can keep, forward or show someone.
function PayslipDetailButton({ sorter, unlock, from, to, label }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try {
      const r = await appsscript('getSorterPayslipDetail', { login: sorter.login, unlock, from, to });
      if (!r || !r.success) throw new Error((r && r.error) || 'Could not build the statement');
      const { downloadSorterPayslip } = await import('../utils/sorterPayslip');
      await downloadSorterPayslip(r);
    } catch (e) { setErr(e.message || 'Could not build the statement'); }
    setBusy(false);
  };
  return (
    <>
      <button onClick={go} disabled={busy} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', marginTop: 12, padding: '11px 14px', borderRadius: 9, border: '1px solid var(--hp-gold)', background: 'transparent', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.84rem', fontFamily: 'var(--font-body)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <FileText size={15} />}
        {busy ? 'Building…' : (label || 'Download the detailed statement')}
      </button>
      {err && <div style={{ color: 'var(--hp-red, #EF4444)', fontSize: '0.76rem', marginTop: 6, textAlign: 'center' }}>{err}</div>}
    </>
  );
}

function PayslipsTab({ sorter, unlock, onLocked }) {
  const [data, setData] = useState(null);
  const [earn, setEarn] = useState(null);
  const [open, setOpen] = useState(null);
  const [showShifts, setShowShifts] = useState(false);
  useEffect(() => {
    appsscript('getSorterPayslips', { login: sorter.login, unlock }).then(r => {
      if (r && r.error === 'VAULT_LOCKED') { if (onLocked) onLocked(); return; }
      setData(r && r.success ? r : { payslips: [], ytd: {} });
    }).catch(() => setData({ payslips: [], ytd: {} }));
    appsscript('getSorterEarnings', { login: sorter.login, unlock })
      .then(r => setEarn(r && r.success ? r : null)).catch(() => setEarn(null));
  }, [sorter.login, unlock, onLocked]);
  if (data === null) return <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>;
  const ps = data.payslips || [], ytd = data.ytd || {};
  const dm = (iso) => new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  const dow = (iso) => new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short' });
  const paid = (earn && earn.detail || []).filter(x => x.amount != null);
  const unapproved = (earn && earn.detail || []).filter(x => x.amount == null);

  return (
    <>
      {/* This period, before tax. The 4-week cycle you're paid on — same period as the
          drivers, and the same one Xero pays from. Gross ONLY: tax comes out in Xero and
          this screen must not put a number in front of someone that their payslip won't
          agree with. */}
      {earn && (
        <div style={{ ...S.card, borderColor: 'var(--hp-gold)' }}>
          <div style={S.label}>This period · {dm(earn.period.from)} – {dm(earn.period.to)}</div>
          <div style={{ fontSize: '2.1rem', fontWeight: 800, fontFamily: 'var(--font-mono, monospace)', marginTop: 6, lineHeight: 1.1 }}>{money(earn.grossBeforeTax)}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-gold)', fontWeight: 700, marginTop: 2 }}>Before tax and other deductions</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 8 }}>
            {earn.hours} hours over {earn.shifts === 1 ? '1 shift' : `${earn.shifts} shifts`}
            {earn.rate ? <> · paid {money(earn.rate.total)} an hour ({earn.level})</> : null}
          </div>


          {earn.rate && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 12px', marginTop: 12, fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--hp-dim)' }}>Ordinary hours</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>{money(earn.categories.ordinary.amount)}</span>
              <span style={{ color: 'var(--hp-dim)' }}>Night shift penalty</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>{money(earn.categories.night.amount)}</span>
              <span style={{ color: 'var(--hp-dim)' }}>Casual loading</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>{money(earn.categories.casual.amount)}</span>
              {!!earn.categories.ot1.hours && <><span style={{ color: 'var(--hp-dim)' }}>Overtime</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>{money(earn.categories.ot1.amount + earn.categories.ot2.amount)}</span></>}
              <span style={{ fontWeight: 700, borderTop: '1px solid var(--hp-border)', paddingTop: 4 }}>Before tax</span>
              <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, borderTop: '1px solid var(--hp-border)', paddingTop: 4 }}>{money(earn.grossBeforeTax)}</span>
              <span style={{ color: 'var(--hp-dim)' }}>Super paid on top (12%)</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', color: 'var(--hp-dim)' }}>{money(earn.superAmt)}</span>
            </div>
          )}

          {!!paid.length && (
            <>
              <button onClick={() => setShowShifts(v => !v)} style={{ background: 'transparent', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.8rem', padding: '10px 0 0', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                {showShifts ? 'Hide' : 'Show'} the {paid.length} approved {paid.length === 1 ? 'shift' : 'shifts'}
              </button>
              {showShifts && (
                <div style={{ marginTop: 6 }}>
                  {paid.map((x, i) => (
                    <div key={x.date + i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', borderTop: '1px solid var(--hp-border)', fontSize: '0.82rem' }}>
                      <span style={{ width: 92, color: 'var(--hp-dim)' }}>{dow(x.date)} {dm(x.date)}</span>
                      <span style={{ width: 54, fontFamily: 'var(--font-mono, monospace)' }}>{x.hours} h</span>
                      <span style={{ flex: 1, color: 'var(--hp-dim)', fontSize: '0.76rem' }}>{x.slot}</span>
                      <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{money(x.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <p style={{ ...S.sub, marginTop: 12 }}>
            You&apos;re paid on a 4-week period, the same cycle as the drivers. Tax and any other deductions come off this
            figure when the pay run is processed — your payslip is the final word on what lands in your account.
          </p>
          <PayslipDetailButton sorter={sorter} unlock={unlock} from={earn.period.from} to={earn.period.to} />
        </div>
      )}

      {/* Unapproved hours: HOURS ONLY, never a dollar figure. They aren't earnings until a
          manager approves them, and putting money against them would promise pay that
          hasn't been agreed — but hiding them would let someone reach payday short without
          ever having been told. */}
      {earn && !!unapproved.length && (
        <div style={{ ...S.card, borderColor: 'var(--hp-amber, #F59E0B)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <AlertTriangle size={15} color="var(--hp-amber, #F59E0B)" />
            <div style={{ ...S.label, margin: 0 }}>Unapproved hours</div>
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 800, fontFamily: 'var(--font-mono, monospace)', marginTop: 6 }}>
            {earn.awaiting.hours} {earn.awaiting.hours === 1 ? 'hour' : 'hours'}
          </div>
          <div style={{ marginTop: 8 }}>
            {unapproved.map((x, i) => (
              <div key={x.date + i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', borderTop: '1px solid var(--hp-border)', fontSize: '0.82rem' }}>
                <span style={{ width: 92, color: 'var(--hp-dim)' }}>{dow(x.date)} {dm(x.date)}</span>
                <span style={{ width: 54, fontFamily: 'var(--font-mono, monospace)' }}>{x.hours} h</span>
                <span style={{ flex: 1, color: 'var(--hp-dim)', fontSize: '0.76rem' }}>{x.slot}</span>
                <span style={{ color: 'var(--hp-amber, #F59E0B)', fontSize: '0.76rem' }}>{x.status === 'Discarded' ? 'not counted' : 'unapproved'}</span>
              </div>
            ))}
          </div>
          <p style={{ ...S.sub, marginTop: 10 }}>
            These hours are waiting on a manager and are <strong>not</strong> in the figure above. Once they&apos;re approved
            they&apos;ll move across and be paid with the rest of the period.
          </p>
        </div>
      )}

      <div style={S.card}>
        <div style={S.label}>This financial year</div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8 }}>
          {[['Earned before tax', earn ? earn.fyGrossBeforeTax : ytd.gross, true],
            ['Tax withheld', ytd.payg, ytd.taxKnown],
            ['Super', earn ? earn.fySuperAmt : ytd.super, true]].map(([l, v, show]) => (
            <div key={l}>
              <div style={{ fontSize: '0.66rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{l}</div>
              <div style={{ fontWeight: 800, fontFamily: 'var(--font-mono, monospace)', fontSize: '1.05rem' }}>{show ? money(v) : '—'}</div>
            </div>
          ))}
        </div>
        <p style={{ ...S.sub, marginTop: 8 }}>
          Earnings include this period so far.{ytd.taxKnown ? '' : ' Tax withheld covers the periods already paid — this period is not taxed until the pay run is processed.'}
        </p>
      </div>

      {!ps.length ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '36px 20px' }}><DollarSign size={28} color="var(--hp-dim)" /><p style={S.sub}>No finished periods yet — this period&apos;s pay is above.</p></div>
      ) : (
        <div style={S.card}>
          <h2 style={S.sectionTitle}>My payslips</h2>
          {ps.map((p, i) => {
            const key = (p['Period Start'] || '') + i, isOpen = open === key;
            return (
              <div key={key} style={{ borderBottom: '1px solid var(--hp-border)', padding: '10px 0' }}>
                <div onClick={() => setOpen(isOpen ? null : key)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{dm(p['Period Start'])} – {dm(p['Period End'])}</div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>
                      {p['Total Hours'] ? `${p['Total Hours']} h · ` : ''}{p.Source === 'xero'
                        ? (p['Payment Date']
                          ? `${p['Payment Date'] <= todayISO() ? 'paid' : 'pays'} ${dm(p['Payment Date'])}`
                          : 'paid')
                        : 'not yet paid'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 800, fontFamily: 'var(--font-mono, monospace)' }}>{money(p.Source === 'xero' ? p.Net : p.Gross)}</div>
                    <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', letterSpacing: '0.04em' }}>{p.Source === 'xero' ? (p['Payment Date'] && p['Payment Date'] > todayISO() ? 'DUE' : 'PAID') : 'BEFORE TAX'}</div>
                  </div>
                  <ChevronRight size={16} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', color: 'var(--hp-dim)', transition: 'transform 0.15s' }} />
                </div>
                {isOpen && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '5px 12px', fontSize: '0.84rem' }}>
                      {!!p['Total Hours'] && <><span style={{ color: 'var(--hp-dim)' }}>Hours</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>{p['Total Hours']} h</span></>}
                      <span style={{ fontWeight: 700 }}>Before tax and other deductions</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', fontWeight: 700 }}>{money(p.Gross)}</span>
                      {p.Source === 'xero' ? <>
                        <span style={{ color: 'var(--hp-dim)' }}>Tax withheld</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>−{money(p.PAYG)}</span>
                        {!!p.Deductions && <><span style={{ color: 'var(--hp-dim)' }}>Other deductions</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>−{money(p.Deductions)}</span></>}
                        {!!p.Reimbursements && <><span style={{ color: 'var(--hp-dim)' }}>Reimbursements</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>+{money(p.Reimbursements)}</span></>}
                        <span style={{ fontWeight: 700, borderTop: '1px solid var(--hp-border)', paddingTop: 4 }}>Paid to you</span>
                        <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, borderTop: '1px solid var(--hp-border)', paddingTop: 4 }}>{money(p.Net)}</span>
                      </> : null}
                      <span style={{ color: 'var(--hp-dim)' }}>Super paid on top</span><span style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>{money(p.Super)}</span>
                    </div>
                    <p style={{ ...S.sub, marginTop: 8 }}>
                      {p.Source === 'xero'
                        ? 'These are the figures from the processed pay run — the official record of what was paid.'
                        : 'This period hasn\u2019t been through a pay run yet, so the tax and take-home aren\u2019t final.'}
                    </p>
                    <PayslipDetailButton sorter={sorter} unlock={unlock} from={p['Period Start']} to={p['Period End']}
                      label="Download the detailed statement" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── My Super ──────────────────────────────────────────────────────────────────
function SuperTab({ sorter, unlock, onLocked }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    appsscript('getSorterPayslips', { login: sorter.login, unlock }).then(r => {
      if (r && r.error === 'VAULT_LOCKED') { if (onLocked) onLocked(); return; }
      setData(r && r.success ? r : { payslips: [], ytd: {} });
    }).catch(() => setData({ payslips: [], ytd: {} }));
  }, [sorter.login, unlock, onLocked]);
  if (data === null) return <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>;
  const ps = data.payslips || [], ytd = data.ytd || {};
  return (
    <>
      <div style={{ ...S.card, textAlign: 'center' }}>
        <PiggyBank size={32} color="var(--hp-gold)" />
        <div style={{ ...S.label, marginTop: 10 }}>Super this financial year</div>
        <div style={{ fontSize: '2rem', fontWeight: 800, fontFamily: 'var(--font-mono, monospace)' }}>{money(ytd.super)}</div>
        <p style={S.sub}>Paid at 12% of your gross into your nominated fund.</p>
      </div>
      {ps.length > 0 && (
        <div style={S.card}>
          <h2 style={S.sectionTitle}>By period</h2>
          {ps.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--hp-border)', fontSize: '0.86rem' }}>
              <span>{p['Period Start']} – {p['Period End']}</span>
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700 }}>{money(p.Super)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── My Documents — onboarding (the working P0 tab) ────────────────────────────
// Gated by a personal step-up password: on first open the sorter sets one, and on
// every open after that they enter it to unlock TFN / super / bank / ID documents.
function DocumentsTab({ sorter, onDocsCount }) {
  const login = sorter.login;
  const [gate, setGate] = useState('loading');       // 'loading' | 'locked' | 'open'
  const [hasPassword, setHasPassword] = useState(false);
  const [unlock, setUnlock] = useState('');
  const [status, setStatus] = useState(null);
  const [toast, setToast] = useState(null);
  const flash = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  // Reuse a still-valid session token, otherwise ask whether a password exists yet.
  useEffect(() => {
    const t = readUnlock(login);
    if (t) { setUnlock(t); setGate('open'); return; }
    let live = true;
    vaultFetch({ op: 'pwdStatus', login })
      .then(d => { if (live) { setHasPassword(!!(d && d.hasPassword)); setGate('locked'); } })
      .catch(() => { if (live) { setHasPassword(false); setGate('locked'); } });
    return () => { live = false; };
  }, [login]);

  const relock = useCallback(() => {
    clearUnlock(login); setUnlock(''); setStatus(null); setHasPassword(true); setGate('locked');
    flash('Locked — enter your password to continue.', false);
  }, [login]);

  const loadStatus = useCallback(async () => {
    if (!unlock) return;
    try {
      const data = await vaultFetch({ op: 'getStatus', login, unlock });
      if (data.success) setStatus(data);
      else if (data.error === 'VAULT_LOCKED') relock();
    } catch {}
  }, [login, unlock, relock]);
  useEffect(() => { if (gate === 'open') loadStatus(); }, [gate, loadStatus]);

  function onUnlocked(tok) { setUnlock(tok); saveUnlock(login, tok); setGate('open'); }

  if (gate === 'loading') return <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>;
  if (gate !== 'open') return <VaultLock login={login} hasPassword={hasPassword} onUnlocked={onUnlocked} />;

  return (
    <>
      {toast && (
        <div style={{ ...S.pill(toast.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), marginBottom: 14, padding: '8px 12px' }}>
          {toast.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {toast.msg}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ ...S.pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)') }}><ShieldCheck size={13} /> Unlocked</span>
        <button style={{ ...S.ghostBtn, marginLeft: 'auto' }} onClick={relock}><Lock size={13} /> Lock</button>
      </div>
      {/* The compliance checklist — the same engine the drivers use (driver-dms-sb),
          scoped to the night-sorter document set. Identity documents are shared:
          someone who both sorts and drives uploads their passport once. */}
      <MyDmsPortalTab onCountChange={onDocsCount} onVaultChanged={loadStatus} />

      {/* How to get, sign and upload the two forms. The upload is the checklist above;
          the TFN read off the form goes to the encrypted vault, never the document store. */}
      <TaxSuperFormsCard login={login} />
      <BankCard login={login} unlock={unlock} onSaved={loadStatus} onLocked={relock} flash={flash} />
      <UploadCard login={login} status={status} unlock={unlock} onSaved={loadStatus} onLocked={relock} flash={flash} />
      <StatementsCard />
    </>
  );
}

// ── Vault lock screen — create (first time), enter, or self-reset the password ─
function VaultLock({ login, hasPassword, onUnlocked, what }) {
  const noun = what || 'your tax file number, super and bank details';
  // 'create' first time · 'enter' when one exists · 'reset' = forgot → PIN + new password.
  const [mode, setMode] = useState(hasPassword ? 'enter' : 'create');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const needsConfirm = mode === 'create' || mode === 'reset';
  const title = mode === 'create' ? 'Protect your details' : mode === 'reset' ? 'Reset your password' : 'Enter your password';

  function switchMode(m) { setMode(m); setErr(''); setPw(''); setPw2(''); setPin(''); }

  async function submit() {
    setErr('');
    if (mode === 'reset' && !pin.trim()) { setErr('Enter your login PIN to confirm it’s you.'); return; }
    if (pw.length < 6) { setErr('Use at least 6 characters.'); return; }
    if (needsConfirm && pw !== pw2) { setErr("The two passwords don't match."); return; }
    setBusy(true);
    try {
      const op = mode === 'enter' ? 'pwdVerify' : mode === 'reset' ? 'pwdResetSelf' : 'pwdSet';
      const body = mode === 'reset' ? { op, login, pin: pin.trim(), password: pw } : { op, login, password: pw };
      const d = await vaultFetch(body);
      if (!d.success || !d.unlock) throw new Error(d.error || (mode === 'enter' ? 'Incorrect password.' : 'Could not set your password.'));
      onUnlocked(d.unlock);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Lock size={18} color="var(--hp-gold)" />
        <h2 style={{ ...S.sectionTitle, flex: 1 }}>{title}</h2>
      </div>
      <p style={S.sub}>
        {mode === 'create'
          ? 'Set a personal password to lock your pay and personal details — payslips, tax file number, super and bank. You’ll enter it each time you open a protected tab. It’s yours alone — we only ever store it scrambled, so no one can read it back.'
          : mode === 'reset'
            ? 'Forgot your password? Confirm it’s you with your login PIN, then set a new one. Nothing you’ve saved is lost.'
            : `This tab holds ${noun}. Enter your personal password to unlock it.`}
      </p>
      {mode === 'reset' && (<>
        <label style={S.label}>Login PIN</label>
        <input style={S.input} type="password" inputMode="numeric" value={pin} autoFocus autoComplete="off"
          onChange={e => setPin(e.target.value)} placeholder="The PIN you sign in with" />
      </>)}
      <label style={S.label}>{mode === 'enter' ? 'Password' : 'New password'}</label>
      <input style={S.input} type="password" value={pw} autoFocus={mode !== 'reset'} autoComplete={mode === 'enter' ? 'current-password' : 'new-password'}
        onChange={e => setPw(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && mode === 'enter' && submit()} placeholder="At least 6 characters" />
      {needsConfirm && (<>
        <label style={S.label}>Confirm password</label>
        <input style={S.input} type="password" value={pw2} autoComplete="new-password"
          onChange={e => setPw2(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Re-enter it" />
      </>)}
      {err && <div style={{ ...S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), marginBottom: 12 }}><AlertTriangle size={13} />{err}</div>}
      <button style={S.bigBtn('var(--hp-gold)')} disabled={busy} onClick={submit}>
        {busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : (mode === 'create' ? 'Set password & continue' : mode === 'reset' ? 'Reset & continue' : <>Unlock <ChevronRight size={18} /></>)}
      </button>
      {mode === 'enter' && (
        <button onClick={() => switchMode('reset')} style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--hp-gold)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'var(--font-body)' }}>
          Forgot your password?
        </button>
      )}
      {mode === 'reset' && (
        <button onClick={() => switchMode('enter')} style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'var(--font-body)' }}>
          ← Back to sign in
        </button>
      )}
      {mode === 'create' && <p style={{ ...S.sub, marginTop: 12, marginBottom: 0 }}>Choose something only you know. If you forget it, you can reset it here with your PIN.</p>}
    </div>
  );
}

// Wrap a tab's content behind the same personal step-up password that guards Documents.
// Reuses the session unlock token, so a sorter who already unlocked one protected tab
// isn't re-prompted. Renders children as a function: (unlock, relock) => <Tab …/>.
function VaultGate({ login, what, children }) {
  const [gate, setGate] = useState('loading');   // 'loading' | 'locked' | 'open'
  const [hasPassword, setHasPassword] = useState(false);
  const [unlock, setUnlockTok] = useState('');
  useEffect(() => {
    const t = readUnlock(login);
    if (t) { setUnlockTok(t); setGate('open'); return; }
    let live = true;
    vaultFetch({ op: 'pwdStatus', login })
      .then(d => { if (live) { setHasPassword(!!(d && d.hasPassword)); setGate('locked'); } })
      .catch(() => { if (live) { setHasPassword(false); setGate('locked'); } });
    return () => { live = false; };
  }, [login]);
  const relock = useCallback(() => { clearUnlock(login); setUnlockTok(''); setHasPassword(true); setGate('locked'); }, [login]);
  function onUnlocked(tok) { setUnlockTok(tok); saveUnlock(login, tok); setGate('open'); }
  if (gate === 'loading') return <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>;
  if (gate !== 'open') return <VaultLock login={login} hasPassword={hasPassword} onUnlocked={onUnlocked} what={what} />;
  return children(unlock, relock);
}

// ── Tax & super forms: get them, sign them, upload them ──────────────────────
// UPLOAD ONLY (Paul, 17 Sep 2026). The typed-in TFN declaration and super nomination
// were retired: the office has to be able to open the completed, signed form. This card
// is the instructions; the upload itself is the TFN Declaration / Superannuation
// Standard Choice Form items in the checklist above (driver-dms-sb), which read the
// TFN into the encrypted vault. The ATO no longer publishes a downloadable TFN form —
// payees complete New employment in myGov and give the employer the summary — so that
// is the route shown, with the paper form as the fallback.
function TaxSuperFormsCard({ login }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    vaultFetch({ op: 'onboardingStatus', login })
      .then(d => { if (alive && d && d.success) setInfo(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [login]);
  const emp = info?.employer || {};
  const superUrl = info?.superFormUrl || '/forms/super-standard-choice-form.pdf';
  const step = (n, title, body) => (
    <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderTop: '1px solid var(--hp-border)' }}>
      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 999, background: 'rgba(245,158,11,0.14)', color: 'var(--hp-gold)', fontWeight: 800, fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
      <div style={{ flex: 1, minWidth: 0, fontSize: '0.84rem', lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>{title}</div>
        {body}
      </div>
    </div>
  );
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <FileText size={18} color="var(--hp-gold)" />
        <h2 style={{ ...S.sectionTitle, flex: 1 }}>Your tax &amp; super forms</h2>
      </div>
      <p style={S.sub}>We need two signed forms before we can pay you: a <strong>TFN declaration</strong> and a <strong>super choice form</strong>. Fill them in, then upload them in the checklist above.</p>
      {step(1, 'TFN declaration — do it in myGov', (
        <>
          Sign in to <a href="https://my.gov.au" target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)' }}>myGov</a>, open the <strong>Australian Taxation Office</strong>, then <strong>Employment → New employment</strong>.
          {emp.abn && <> Our ABN is <strong style={{ fontFamily: 'var(--font-mono, monospace)' }}>{emp.abn}</strong>{emp.name ? ` (${emp.name})` : ''}.</>}
          {' '}Submit it, then <strong>save or screenshot the summary</strong> the ATO shows you.
          <div style={{ color: 'var(--hp-dim)', fontSize: '0.76rem', marginTop: 4 }}>No TFN yet, or can't use myGov? Ask the office for a paper TFN declaration form, fill it in and sign it.</div>
        </>
      ))}
      {step(2, 'Super choice form — download it', (
        <>
          <a href={superUrl} target="_blank" rel="noreferrer" style={{ ...S.ghostBtn, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', margin: '4px 0' }}><Download size={14} /> Download the super choice form (PDF)</a>
          <div>Fill in your details and your fund's details, and sign it. If you already chose your fund in myGov's New employment, that summary works too.</div>
        </>
      ))}
      {step(3, 'Upload both', (
        <>In the checklist above, open <strong>TFN Declaration</strong> and <strong>Superannuation Standard Choice Form</strong> and upload each one — a clear photo of every page, or the PDF.</>
      ))}
    </div>
  );
}

// ── Where the wages go ────────────────────────────────────────────────────────
// The one part of a sorter's pay setup nobody else can supply. It sits behind the
// same step-up password as the TFN and super cards, and the server validates the
// BSB and account number again before writing — a transposed digit here isn't found
// until a payment bounces a fortnight later.
//
// What's already on file is shown MASKED (institution + last three digits): enough
// to recognise their own account, not enough for a shoulder-surfer to copy it.
function BankCard({ login, unlock, onSaved, onLocked, flash }) {
  const [bank, setBank] = useState(null);   // null = loading
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ bankName: '', accountName: '', bsb: '', accNumber: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    vaultFetch({ op: 'onboardingStatus', login })
      .then(d => setBank((d && d.success && d.bank) || { has: false }))
      .catch(() => setBank({ has: false }));
  }, [login]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));

  async function submit() {
    const bsb = f.bsb.replace(/\D/g, ''), acc = f.accNumber.replace(/\D/g, '');
    if (!f.accountName.trim()) { flash('Enter the name on the account.', false); return; }
    if (bsb.length !== 6) { flash('BSB must be 6 digits — the number printed as 062-000.', false); return; }
    if (acc.length < 4 || acc.length > 10) { flash('Account number must be between 4 and 10 digits.', false); return; }
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'saveSorterBankDetails', login, unlock, ...f, bsb, accNumber: acc }),
      });
      const data = await res.json();
      if (!data.success) { if (data.error === 'VAULT_LOCKED') { onLocked(); return; } throw new Error(data.error || 'Could not save.'); }
      flash('Bank details saved — your pay will go to this account.');
      setF({ bankName: '', accountName: '', bsb: '', accNumber: '' });
      setOpen(false); load(); onSaved();
    } catch (e) { flash(e.message, false); }
    finally { setBusy(false); }
  }

  const onFile = bank && bank.has;
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Landmark size={18} color="var(--hp-gold)" />
        <h2 style={{ ...S.sectionTitle, flex: 1 }}>Bank account for your pay</h2>
        {bank && (
          <span style={S.pill(onFile ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.14)', onFile ? 'var(--hp-green, #22C55E)' : 'var(--hp-amber, #F59E0B)')}>
            {onFile ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />} {onFile ? 'On file' : 'Not provided'}
          </span>
        )}
      </div>
      {onFile ? (
        <p style={S.sub}>
          We pay into {bank.institution ? bank.institution : 'the account'}{bank.last3 ? ` ••••${bank.last3}` : ''}.
          Changed banks? Enter the new account below — it replaces the old one from your next pay run.
        </p>
      ) : (
        <p style={S.sub}>Tell us the account your wages should be paid into. It must be an account in your own name.</p>
      )}
      {!open && <button style={S.ghostBtn} onClick={() => setOpen(true)}>{onFile ? 'Change account' : 'Add bank details'}</button>}
      {open && (
        <div>
          <label style={S.label}>Institution (your bank)</label>
          <input style={S.input} value={f.bankName} onChange={set('bankName')} placeholder="e.g. Commonwealth Bank" />
          <label style={S.label}>Account name</label>
          <input style={S.input} value={f.accountName} onChange={set('accountName')} placeholder="The name on the account" />
          <div style={S.row}>
            <div style={{ flex: '0 0 130px' }}>
              <label style={S.label}>BSB</label>
              <input style={S.input} inputMode="numeric" autoComplete="off" value={f.bsb} onChange={set('bsb')} placeholder="062-000" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Account number</label>
              <input style={S.input} inputMode="numeric" autoComplete="off" value={f.accNumber} onChange={set('accNumber')} placeholder="12345678" />
            </div>
          </div>
          <p style={{ ...S.sub, marginTop: 8 }}>Check these against your banking app before you save — we pay exactly what's entered here.</p>
          <div style={S.row}>
            <button style={{ ...S.bigBtn('var(--hp-gold)'), flex: 1 }} disabled={busy} onClick={submit}>{busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Save'}</button>
            <button style={S.ghostBtn} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadCard({ login, status, unlock, onSaved, onLocked, flash }) {
  const [busy, setBusy] = useState(false);
  const docs = status?.docs || [];

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const dataBase64 = await fileToBase64(file);
      const data = await vaultFetch({ op: 'storeDoc', login, by: login, unlock, docType: 'Onboarding', filename: file.name, mime: file.type, dataBase64 });
      if (!data.success) { if (data.error === 'VAULT_LOCKED') { onLocked(); return; } throw new Error(data.error || 'Upload failed.'); }
      flash('Document uploaded securely.');
      onSaved();
    } catch (err) { flash(err.message, false); }
    finally { setBusy(false); }
  }

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Upload size={18} color="var(--hp-gold)" />
        <h2 style={{ ...S.sectionTitle, flex: 1 }}>My documents</h2>
      </div>
      <p style={S.sub}>Upload ID, a super statement, or any signed forms. Files are encrypted at rest.</p>
      {docs.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px' }}>
          {docs.map(d => (
            <li key={d.docId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--hp-border)', fontSize: '0.85rem' }}>
              <FileText size={14} color="var(--hp-dim)" />
              <span style={{ flex: 1 }}>{d.filename}</span>
              <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>{(d.uploadedAt || '').slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      )}
      <label style={{ ...S.ghostBtn, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Upload size={14} />} {busy ? 'Uploading…' : 'Upload a document'}
        <input type="file" hidden disabled={busy} onChange={onFile} />
      </label>
    </div>
  );
}

function StatementsCard() {
  const [ack, setAck] = useState(false);
  return (
    <div style={S.card}>
      <h2 style={S.sectionTitle}>Your rights at work</h2>
      <p style={S.sub}>Please read these two statements every new employee must receive.</p>
      <ul style={{ margin: '0 0 14px', paddingLeft: 18, fontSize: '0.9rem', lineHeight: 1.7 }}>
        <li><a style={{ color: 'var(--hp-gold)' }} target="_blank" rel="noreferrer" href="https://www.fairwork.gov.au/employment-conditions/information-statements/fair-work-information-statement">Fair Work Information Statement</a></li>
        <li><a style={{ color: 'var(--hp-gold)' }} target="_blank" rel="noreferrer" href="https://www.fairwork.gov.au/employment-conditions/information-statements/casual-employment-information-statement">Casual Employment Information Statement</a></li>
      </ul>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
        <span style={{ fontSize: '0.88rem' }}>I confirm I have received and read both statements</span>
      </label>
    </div>
  );
}
